// src/pages/ExpertDashboard.jsx
import { valuationText } from "../utils/valuationDisclosure";
import React, { useEffect, useState, useCallback, useRef, Suspense, lazy, useMemo } from "react";
import "../styles/ExpertDashboardMobile.css";
import { supabase } from "@/lib/supabaseClient";
import { useAuth } from "@/context/AuthContext";
import { VISION_URL } from "@/config/domains";
import { toast } from "react-toastify";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import {
  Mail,
  TrendingUp,
  Users,
  AlertTriangle,
  FileText,
  Clock,
  Package,
  Activity,
  Clipboard,
  BarChart2,
  Warehouse,
  BookOpen,
  ExternalLink,
  ShieldCheck,  
  CreditCard,
  UserPlus, 
  UserCircle 
} from "lucide-react";
import { Trans, useTranslation } from "react-i18next";
import ExpertIdentitySection from "@/components/ExpertIdentitySection";
import AdvisorCockpit from "@/components/advisor/AdvisorCockpit";
import ExpertCircleReferralCard from "@/components/ExpertCircleReferralCard";
import AdvisorReadinessQueue from "@/components/advisor/AdvisorReadinessQueue";
import AdvisorClientDossiers from "@/components/advisor/AdvisorClientDossiers";
import { computeAdvisorOpportunities, OPPORTUNITY_SELECT_SECURE } from "@/utils/advisorOpportunities";
import { computeArtworkReadiness } from "@/utils/readiness";
import { ChevronDown } from "lucide-react";
import AdvisorBillingIdentity from "@/components/advisor/AdvisorBillingIdentity";
import ExpertProfileTab from "@/components/ExpertProfileTab";
import { useMessageNotifications } from "@/hooks/useMessageNotifications";
import ExpertClientAccessStatus from "@/components/ExpertClientAccessStatus";
import CollaboratorsManager from "@/components/CollaboratorsManager";
import { applyProposalFilter, proposalCollectionIds } from "../lib/proposalExclusion";


const SharedDashboard = lazy(() => import("../components/SharedDashboard"));
const ExpertActualitesModule = lazy(() => import("../components/ExpertActualitesModule"));
const ClientManagement = lazy(() => import("../components/ClientManagement"));
const ExpertBillingManager = lazy(() => import("../components/billing/ExpertBillingManager"));
const AdvisorMissions = lazy(() => import("./expert/AdvisorMissions"));


class SharedErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }
  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }
  componentDidCatch(error) {
    console.error("[SharedDashboard] load error:", error);
  }
  render() {
    if (this.state.hasError) {
      return (
        <div
          style={{
            padding: 24,
            background: "#fef2f2",
            border: "1px solid #fecaca",
            borderRadius: 2,
            color: "#991b1b",
            fontFamily: "Georgia, serif",
          }}
        >
          {this.props.message || "SharedDashboard could not be loaded."}
        </div>
      );
    }
    return this.props.children;
  }
}

export default function ExpertDashboard() {
  const auth = useAuth();
  const user = auth?.user || null;
const [profile, setProfile] = useState(null);

useEffect(() => {
  const fetchProfile = async () => {
    const { data, error } = await supabase
      .from("profiles")
      .select("*")
      .single();

    if (!error) setProfile(data);
  };

  fetchProfile();
}, []);

  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // ------------------------------------------------------------
  // ✅ Tabs pilotés par l'URL (source de vérité unique)
  // ------------------------------------------------------------
  const VALID_TABS = useRef(new Set(["overview", "collections", "services", "clients", "outils", "billing", "profile", "collaborateurs", "missions"]));

  const tabParam = searchParams.get("tab");
  const tab = VALID_TABS.current.has(tabParam) ? tabParam : "overview";

  const setTab = useCallback(
    (next) => {
      const safe = VALID_TABS.current.has(next) ? next : "overview";
      setSearchParams((prev) => {
        const p = new URLSearchParams(prev);
        p.set("tab", safe);
        return p;
      }, { replace: true });
    },
    [setSearchParams]
  );
  // ------------------------------------------------------------
  // States existants (inchangés)
  // ------------------------------------------------------------
  const [serviceRequests, setServiceRequests] = useState([]);
  const [, setLoadingServices] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  const loaded = useRef(false);
  const [, setSharedCount] = useState(0);

  // Invites / clients
  const [, setInvites] = useState([]);
  const [clientInvites, setClientInvites] = useState([]); // expert_clients (pending)
  const [clientInvitesCount, setClientInvitesCount] = useState(0);
  // Mise en relation payante (49 € HT) pour les experts non abonnés.
  const [introModal, setIntroModal] = useState(null); // { inviteId }
  const [introBusy, setIntroBusy] = useState(false);
  // Indication d'affichage uniquement : la décision réelle est prise côté serveur (trigger + fonction expert-introduction).
  const introIncluded = useMemo(() => {
    const p = profile || {};
    const planOk = ["art_advisor", "art_advisor_premium"].includes(p.plan);
    const active =
      p.is_demo === true ||
      (p.free_until && new Date(p.free_until) > new Date()) ||
      ["active", "trialing"].includes(p.stripe_subscription_status);
    return !!(planOk && active);
  }, [profile]);
  const [, setInvitesCount] = useState(0);
  const [, setIncomingClientInvitesCount] = useState(0);

  // Invite modal (advisor_invites)
  const [showInvite, setShowInvite] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteNote, setInviteNote] = useState("");
  const [sendingInvite, setSendingInvite] = useState(false);

  // 🆕 Modal de confirmation mise en contact
  const [showAcceptModal, setShowAcceptModal] = useState(false);
  const [requestToAccept, setRequestToAccept] = useState(null);

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/i;

  const { t, i18n } = useTranslation();
  const locale = useMemo(() => {
    const lang = String(i18n?.resolvedLanguage || i18n?.language || "fr").toLowerCase();
    if (lang.startsWith("de")) return "de-DE";
    if (lang.startsWith("en")) return "en-US";
    return "fr-FR";
  }, [i18n?.language, i18n?.resolvedLanguage]);
const tx = useCallback(
  (key, fallback, options = {}) => {
    return t(key, {
      defaultValue: fallback,
      ...options,
    });
  },
  [t]
);

const dashboardT = useCallback(
  (key, fallbackOrOptions, options = {}) => {
    const hasFallback = typeof fallbackOrOptions === "string";
    const fallback = hasFallback ? fallbackOrOptions : undefined;
    const resolvedOptions = hasFallback ? options : (fallbackOrOptions || {});
    return tx(`expert.Dashboard.${key}`, fallback, resolvedOptions);
  },
  [tx]
);

  const DEFAULT_EXPERT_IMAGE = "data:image/svg+xml;utf8," + encodeURIComponent(`
<svg xmlns="http://www.w3.org/2000/svg" width="80" height="80" viewBox="0 0 80 80">
  <rect width="80" height="80" rx="40" fill="#f2efe9"/>
  <circle cx="40" cy="31" r="14" fill="#c9a961"/>
  <path d="M18 68c4-16 17-24 22-24s18 8 22 24" fill="#c9a961" opacity="0.75"/>
</svg>
`);

  // Profile basics
  const [profileBasics, setProfileBasics] = useState(null);
const [portfolioSummary, setPortfolioSummary] = useState(null);
const [loadingPortfolio, setLoadingPortfolio] = useState(false);
const [selectedPortfolioClientId, setSelectedPortfolioClientId] = useState("auto");
const [selectedPortfolioCollectionId, setSelectedPortfolioCollectionId] = useState("all");

// Cockpit Advisor (P0) : agrégateur unique via RPC get_advisor_dashboard_summary
const [dashboardSummary, setDashboardSummary] = useState(null);
const [summaryLoading, setSummaryLoading] = useState(true);
const [analysisOpen, setAnalysisOpen] = useState(false);

// Données readiness/opportunités : une seule lecture d'artworks_secure (RLS = mandat),
// partagée par les opportunités (§5) et la file de travail readiness (§6).
const [opportunities, setOpportunities] = useState([]);
const [secureArtworks, setSecureArtworks] = useState([]);
const [collectionNames, setCollectionNames] = useState(new Map());
const [missionBusy, setMissionBusy] = useState(false);

const loadOpportunities = useCallback(async () => {
  if (!user?.id) return;
  try {
    const [{ data: rows, error: rowsErr }, { data: actions }] = await Promise.all([
      applyProposalFilter(supabase.from("artworks_secure").select(OPPORTUNITY_SELECT_SECURE), await proposalCollectionIds(user.id)),
      supabase
        .from("advisor_opportunity_actions")
        .select("opportunity_fingerprint,status,snoozed_until")
        .eq("advisor_id", user.id),
    ]);
    if (rowsErr) { console.warn("[ExpertDashboard] opportunities load", rowsErr); setOpportunities([]); setSecureArtworks([]); return; }
    setSecureArtworks(rows || []);

    // Noms de collections (best-effort, RLS peut restreindre)
    const collIds = [...new Set((rows || []).map((r) => r.collection_id).filter(Boolean))];
    const names = new Map();
    if (collIds.length) {
      const { data: colls } = await supabase.from("collections").select("id,title").in("id", collIds);
      (colls || []).forEach((c) => names.set(c.id, c.title));
    }
    setCollectionNames(names);

    const lang = i18n.resolvedLanguage || i18n.language || "fr";
    let list = computeAdvisorOpportunities(rows || [], { lang, collectionNames: names });

    // Filtre décisions : masquer ignorées, converties, ou en pause non expirée.
    const now = Date.now();
    const byFp = new Map((actions || []).map((a) => [a.opportunity_fingerprint, a]));
    list = list.filter((op) => {
      const a = byFp.get(op.fingerprint);
      if (!a) return true;
      if (a.status === "dismissed" || a.status === "converted") return false;
      if (a.status === "snoozed" && a.snoozed_until && new Date(a.snoozed_until).getTime() > now) return false;
      return true;
    });
    setOpportunities(list.slice(0, 5));
  } catch (e) {
    console.warn("[ExpertDashboard] opportunities failed", e);
    setOpportunities([]);
  }
}, [user?.id, i18n]);

useEffect(() => { loadOpportunities(); }, [loadOpportunities]);

// Crée une mission propre + lie les œuvres (§6 file readiness ET §5 opportunités
// avec œuvres). Retourne l'id créé (ou null). navigateAfter=false pour laisser
// l'appelant orchestrer (ex. marquer l'opportunité convertie avant de naviguer).
const createGroupedMission = useCallback(async ({ artworkIds, missionType, title, clientId, collectionId, status, navigateAfter = true }) => {
  if (!user?.id || !Array.isArray(artworkIds) || artworkIds.length === 0) return null;
  setMissionBusy(true);
  try {
    // Hériter le dossier client (§8bis) depuis la collection si elle en a un.
    let entityId = null;
    if (collectionId) {
      const { data: col } = await supabase.from("collections").select("client_entity_id").eq("id", collectionId).maybeSingle();
      entityId = col?.client_entity_id || null;
    }
    const { data, error } = await supabase
      .from("advisor_missions")
      .insert({
        advisor_id: user.id,
        created_by: user.id,
        title: (title || "Mission").slice(0, 180),
        mission_type: missionType || "other",
        client_id: clientId || null,
        collection_id: collectionId || null,
        client_entity_id: entityId,
        priority: "normal",
        billing_mode: "external",
        status: status || "draft",
        // Rail mission propre : 0 % commission ARTKIV (verrouillé par trigger).
        origin: "advisor_client",
        commercial_model: "advisor_direct",
        billing_owner: "advisor",
      })
      .select("id")
      .single();
    if (error || !data?.id) {
      console.warn("[ExpertDashboard] createGroupedMission", error);
      alert(t("advisorMissions.errors.create", { message: error?.message || "", defaultValue: "Création impossible." }));
      return null;
    }
    const links = artworkIds.map((aid) => ({ mission_id: data.id, artwork_id: aid, created_by: user.id }));
    await supabase.from("advisor_mission_artworks").insert(links);
    await supabase.from("advisor_mission_events").insert({
      mission_id: data.id,
      event_type: "mission_created",
      actor_id: user.id,
      source: "user",
    });
    if (navigateAfter) navigate("/expert/dashboard?tab=missions");
    return data.id;
  } catch (e) {
    console.warn("[ExpertDashboard] createGroupedMission failed", e);
    return null;
  } finally {
    setMissionBusy(false);
  }
}, [user?.id, navigate, t]);

// 5e KPI cockpit : œuvres nécessitant une action (readiness incomplète).
const artworksRequiringAction = useMemo(
  () => secureArtworks.filter((a) => computeArtworkReadiness(a).score < 100).length,
  [secureArtworks]
);

// Noms clients pour la file readiness (réutilise les options portefeuille)
const clientNamesMap = useMemo(() => {
  const m = new Map();
  (portfolioSummary?.clientSummaries || []).forEach((c) => {
    if (c.clientId) m.set(c.clientId, c.clientName || `${c.first_name || ""} ${c.last_name || ""}`.trim());
  });
  return m;
}, [portfolioSummary]);

const recordOpportunityAction = useCallback(async (op, status, extra = {}) => {
  if (!user?.id) return;
  // Retrait optimiste
  setOpportunities((prev) => prev.filter((o) => o.fingerprint !== op.fingerprint));
  try {
    await supabase.from("advisor_opportunity_actions").upsert(
      {
        advisor_id: user.id,
        client_id: op.client_id || null,
        collection_id: op.collection_id || null,
        opportunity_key: op.opportunity_key,
        opportunity_fingerprint: op.fingerprint,
        status,
        updated_at: new Date().toISOString(),
        ...extra,
      },
      { onConflict: "advisor_id,opportunity_fingerprint" }
    );
  } catch (e) {
    console.warn("[ExpertDashboard] recordOpportunityAction", e);
  }
}, [user?.id]);

const goToMissionFromOpportunity = useCallback((op, draft) => {
  const params = new URLSearchParams({ new: "1" });
  if (op.mission_type) params.set("mtype", op.mission_type);
  if (op.mission_title) params.set("mtitle", op.mission_title);
  if (op.client_id) params.set("client", op.client_id);
  if (op.collection_id) params.set("collection", op.collection_id);
  params.set("origin", "opportunity");
  params.set("fp", op.fingerprint);
  if (draft) params.set("draft", "1");
  recordOpportunityAction(op, "converted");
  navigate(`/expert/dashboard?tab=missions&${params.toString()}`);
}, [navigate, recordOpportunityAction]);

// §5 : convertir une opportunité en mission. Si elle porte des œuvres, on les
// LIE réellement (createGroupedMission) ; sinon (ex. rapport patrimonial) on
// bascule vers le formulaire prérempli.
const convertOpportunity = useCallback(async (op, draft) => {
  if (Array.isArray(op?.artwork_ids) && op.artwork_ids.length > 0) {
    const missionId = await createGroupedMission({
      artworkIds: op.artwork_ids,
      missionType: op.mission_type,
      title: op.mission_title,
      clientId: op.client_id,
      collectionId: op.collection_id,
      status: draft ? "draft" : "proposed",
      navigateAfter: false,
    });
    await recordOpportunityAction(op, "converted", missionId ? { converted_mission_id: missionId } : {});
    navigate("/expert/dashboard?tab=missions");
  } else {
    goToMissionFromOpportunity(op, draft);
  }
}, [createGroupedMission, recordOpportunityAction, goToMissionFromOpportunity, navigate]);

useEffect(() => {
  let cancelled = false;
  if (!user?.id) return;
  (async () => {
    setSummaryLoading(true);
    try {
      const { data, error } = await supabase.rpc("get_advisor_dashboard_summary");
      if (cancelled) return;
      if (error) {
        console.warn("[ExpertDashboard] get_advisor_dashboard_summary failed", error);
        setDashboardSummary(null);
      } else {
        setDashboardSummary(data || null);
      }
    } catch (e) {
      if (!cancelled) setDashboardSummary(null);
    } finally {
      if (!cancelled) setSummaryLoading(false);
    }
  })();
  return () => { cancelled = true; };
}, [user?.id]);

  // Expert Profile
  const [expertRow, setExpertRow] = useState(null);
  const [, setLoadingExpert] = useState(false);
  const [savingExpert, setSavingExpert] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [uploadingCv, setUploadingCv] = useState(false);

  const [profileForm, setProfileForm] = useState({
    is_public: false,
    name: "",
    specialty: "",
    circle_kind: "",
    bio: "",
    organization: "",
    location: "",
    contact_email: "",
    contact_phone: "",
    phone: "",
    experience: "",
    credentials: "",
    specializations: "",
    languages: "",
    image_url: "",
  });

  const photoInputRef = useRef(null);
  const cvInputRef = useRef(null);

  // 🔔 Notifications de messages
  const { unreadByConversation, totalUnread, markConversationAsRead, refreshUnreadCount } =
    useMessageNotifications(user?.id);

  const [conversationsByServiceRequest, setConversationsByServiceRequest] = useState({});

  useEffect(() => {
    if (!user?.id) return;

    const loadConversations = async () => {
      const { data, error } = await supabase
        .from("conversations")
        .select("id, context_id")
        .eq("context_type", "expert_service_requests")
        .or(`expert_id.eq.${user.id},client_id.eq.${user.id}`);

      if (error) {
        console.warn("[ExpertDashboard] loadConversations error", error);
        return;
      }

      if (data) {
        const mapping = {};
        data.forEach((c) => {
          if (c.context_id) mapping[c.context_id] = c.id;
        });
        setConversationsByServiceRequest(mapping);
      }
    };

    loadConversations();
  }, [user?.id]);

  const genToken = () => {
    try {
      const a = new Uint8Array(16);
      const wcrypto =
        (typeof window !== "undefined" && (window.crypto || window.msCrypto)) || null;
      if (wcrypto?.getRandomValues) {
        wcrypto.getRandomValues(a);
        return [...a].map((b) => b.toString(16).padStart(2, "0")).join("");
      }
    } catch {
      // ignore
    }
    return Array.from({ length: 32 }, () => Math.floor(Math.random() * 16).toString(16)).join("");
  };

  // ---------- helpers: parsing safe ----------
  const parseList = useCallback((v) => { // eslint-disable-line no-unused-vars
    if (!v) return [];
    if (Array.isArray(v)) return v.filter(Boolean).map(String);
    if (typeof v === "string") {
      return v
        .split(/[,;\n]/g)
        .map((s) => s.trim())
        .filter(Boolean);
    }
    try {
      const parsed = typeof v === "string" ? JSON.parse(v) : v;
      if (Array.isArray(parsed)) return parsed.filter(Boolean).map(String);
    } catch (_) {}
    return [];
  }, []);

  // 1) loadProfileBasics: pas d'email dans profiles
  const euro = useCallback((n) => {
    const value = Number(n || 0);
    return new Intl.NumberFormat(locale, {
      style: "currency",
      currency: "EUR",
      maximumFractionDigits: 0,
    }).format(value);
  }, [locale]);



const compactEuro = useCallback((n) => {
  const value = Number(n || 0);
  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency: "EUR",
    notation: "compact",
    maximumFractionDigits: 1,
  }).format(value);
}, [locale]);

const getArtworkCollectionId = useCallback((row) => {
  return row?.collection_id || row?.collectionId || null;
}, []);

const getArtworkConfidence = useCallback((row) => {
  const candidates = [
    row?.valuation_confidence_score,
  ];

  for (const candidate of candidates) {
    const n = Number(candidate);
    if (Number.isFinite(n) && n > 0) {
      return n;
    }
  }

  return null;
}, []);

const artworkUsesAI = useCallback((row) => {
  return Boolean(
    row?.visual_analysis ||
    row?.valuation_confidence_score ||
    row?.style_ai ||
    row?.certificate_blockchain
  );
}, []);

// ICI  helpers liquidité
const computeLiquidityScore = useCallback((row) => {
  let score = 0;

  const value = Number(
    row?.valuation_market_value ??
    row?.valuation_consensus ??
    row?.current_estimated_value ??
    row?.estimated_value ??
    row?.value ??
    0
  );

  if (value > 0) score += 20;

  const artist = String(row?.artist || "").trim();
  if (artist && artist.toLowerCase() !== "inconnu") score += 15;

  const medium = String(row?.medium || row?.typeOfArt || "").trim();
  if (medium) score += 10;

  const hasDocs =
    (typeof row?.documents === "string" && row.documents.trim().length > 0) ||
    (Array.isArray(row?.documents) && row.documents.length > 0);

  if (hasDocs) score += 10;

  const hasImages = Array.isArray(row?.images) && row.images.length > 0;
  if (hasImages) score += 10;

  if (
    row?.certificate_auth ||
    row?.certificate_blockchain ||
    row?.certificate_authenticity
  ) {
    score += 15;
  }

  if (row?.condition_report || row?.condition_report_url) {
    score += 10;
  }

  const hasComparables =
    Array.isArray(row?.comparables) ||
    Array.isArray(row?.external_comparables) ||
    Array.isArray(row?.market_comparables);

  if (hasComparables) score += 10;

  return {
    score: Math.min(score, 100),
  };
}, []);


const [showAllTopArtists, setShowAllTopArtists] = useState(false);
const [topArtistsMode, setTopArtistsMode] = useState("value");

const ARTWORK_CATEGORY_RULES = useMemo(() => ([
  { key: "photography", label: "Photographie", keywords: ["photo", "photographie", "photography", "tirage", "lambda", "c-print", "c print", "argentique", "diasec", "inkjet", "pigmentaire"] },
  { key: "sculpture", label: "Sculpture", keywords: ["sculpture", "bronze", "marbre", "resine", "résine", "terre cuite", "ceramique", "céramique", "acier", "bois", "platre", "plâtre", "aluminium", "metal", "métal"] },
  { key: "installation", label: "Installation", keywords: ["installation", "assemblage", "environment", "dispositif", "site-specific", "site specific"] },
  { key: "digital", label: "Art numérique / vidéo", keywords: ["digital", "numerique", "numérique", "video", "vidéo", "nft", "film", "animation", "generatif", "génératif", "software"] },
  { key: "print", label: "Estampe & multiples", keywords: ["serigraphie", "sérigraphie", "lithographie", "gravure", "etching", "eau-forte", "aquatinte", "estampe", "print", "multiple", "edition", "édition"] },
  { key: "paper", label: "Œuvres sur papier", keywords: ["papier", "paper", "dessin", "drawing", "aquarelle", "watercolor", "gouache", "pastel", "crayon", "fusain", "encre", "ink", "collage"] },
  { key: "painting", label: "Peinture", keywords: ["peinture", "painting", "huile", "oil", "acrylique", "acrylic", "toile", "canvas", "tempera", "laque"] },
  { key: "design", label: "Design / objet", keywords: ["design", "mobilier", "meuble", "objet", "fauteuil", "chaise", "lampe", "vase"] },
  { key: "mixed_media", label: "Techniques mixtes", keywords: ["mixed media", "technique mixte", "techniques mixtes", "media mixte", "média mixte", "multimedia", "multimédia"] },
]), []);

const normalizeTextForMatch = useCallback((value) => String(value || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().trim(), []);

const CANONICAL_TECHNIQUE_LABELS = useMemo(() => ({
  fr: {
    oil_on_canvas: "Huile sur toile",
    acrylic_on_canvas: "Acrylique sur toile",
    oil_on_paper: "Huile sur papier",
    acrylic_on_paper: "Acrylique sur papier",
    oil_on_panel: "Huile sur panneau",
    acrylic_on_panel: "Acrylique sur panneau",
    watercolor_on_paper: "Aquarelle sur papier",
    gouache_on_paper: "Gouache sur papier",
    ink_on_paper: "Encre sur papier",
    pencil_on_paper: "Crayon sur papier",
    charcoal_on_paper: "Fusain sur papier",
    pastel_on_paper: "Pastel sur papier",
    oil_crayon_on_paper: "Crayon à l'huile sur papier",
    serigraphy: "Sérigraphie",
    lithography: "Lithographie",
    etching: "Eau-forte",
    engraving: "Gravure",
    pigment_print: "Tirage pigmentaire",
    lambda_print: "Tirage lambda",
    c_print: "C-print",
    gelatin_silver_print: "Tirage argentique",
    mixed_media: "Technique mixte",
    photography: "Photographie",
    painting: "Peinture",
    sculpture: "Sculpture",
    print: "Estampe",
    work_on_paper: "Œuvre sur papier",
    drawing: "Dessin",
    digital: "Art numérique",
    film_video: "Film / Vidéo",
    installation: "Installation",
    ceramics: "Céramique",
    textile: "Textile",
    design: "Design",
    other: "Autre",
  },
  en: {
    oil_on_canvas: "Oil on canvas",
    acrylic_on_canvas: "Acrylic on canvas",
    oil_on_paper: "Oil on paper",
    acrylic_on_paper: "Acrylic on paper",
    oil_on_panel: "Oil on panel",
    acrylic_on_panel: "Acrylic on panel",
    watercolor_on_paper: "Watercolor on paper",
    gouache_on_paper: "Gouache on paper",
    ink_on_paper: "Ink on paper",
    pencil_on_paper: "Pencil on paper",
    charcoal_on_paper: "Charcoal on paper",
    pastel_on_paper: "Pastel on paper",
    oil_crayon_on_paper: "Oil crayon on paper",
    serigraphy: "Screenprint",
    lithography: "Lithograph",
    etching: "Etching",
    engraving: "Engraving",
    pigment_print: "Pigment print",
    lambda_print: "Lambda print",
    c_print: "C-print",
    gelatin_silver_print: "Gelatin silver print",
    mixed_media: "Mixed media",
    photography: "Photography",
    painting: "Painting",
    sculpture: "Sculpture",
    print: "Print",
    work_on_paper: "Work on paper",
    drawing: "Drawing",
    digital: "Digital art",
    film_video: "Film / Video",
    installation: "Installation",
    ceramics: "Ceramics",
    textile: "Textile",
    design: "Design",
    other: "Other",
  },
  de: {
    oil_on_canvas: "Öl auf Leinwand",
    acrylic_on_canvas: "Acryl auf Leinwand",
    oil_on_paper: "Öl auf Papier",
    acrylic_on_paper: "Acryl auf Papier",
    oil_on_panel: "Öl auf Tafel",
    acrylic_on_panel: "Acryl auf Tafel",
    watercolor_on_paper: "Aquarell auf Papier",
    gouache_on_paper: "Gouache auf Papier",
    ink_on_paper: "Tinte auf Papier",
    pencil_on_paper: "Bleistift auf Papier",
    charcoal_on_paper: "Kohle auf Papier",
    pastel_on_paper: "Pastell auf Papier",
    oil_crayon_on_paper: "Ölkreide auf Papier",
    serigraphy: "Siebdruck",
    lithography: "Lithografie",
    etching: "Radierung",
    engraving: "Gravur",
    pigment_print: "Pigmentdruck",
    lambda_print: "Lambda-Druck",
    c_print: "C-Print",
    gelatin_silver_print: "Silbergelatineabzug",
    mixed_media: "Mixed Media",
    photography: "Fotografie",
    painting: "Gemälde",
    sculpture: "Skulptur",
    print: "Druckgrafik",
    work_on_paper: "Arbeit auf Papier",
    drawing: "Zeichnung",
    digital: "Digitale Kunst",
    film_video: "Film / Video",
    installation: "Installation",
    ceramics: "Keramik",
    textile: "Textil",
    design: "Design",
    other: "Sonstiges",
  },
}), []);

const getCanonicalLookupKey = useCallback((value) => {
  const raw = String(value || "").trim();
  if (!raw) return "";

  return raw
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[’']/g, "")
    .replace(/&/g, " and ")
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
}, []);

const formatCanonicalTechniqueLabel = useCallback((value) => {
  const raw = String(value || "").trim();
  if (!raw) return dashboardT("portfolio.unknownTechnique", "Technique non renseignée");

  const key = getCanonicalLookupKey(raw);
  const lang = String(i18n?.language || "fr").split("-")[0];
  const labels = CANONICAL_TECHNIQUE_LABELS[lang] || CANONICAL_TECHNIQUE_LABELS.fr;

  const i18nKeys = [
    `artwork.techniques.${key}`,
    `artwork.technique.${key}`,
    `artwork.mediums.${key}`,
    `artwork.medium.${key}`,
    `techniques.${key}`,
    `mediums.${key}`,
    `mediumCategories.${key}`,
    `artworksDashboard.filter.medium.${key}`,
    `artworksDashboard.filter.category.${key}`,
  ];

  for (const translationKey of i18nKeys) {
    if (i18n?.exists?.(translationKey)) return t(translationKey);
  }

  if (labels[key]) return labels[key];

  if (raw.includes("_")) {
    return raw
      .replace(/_/g, " ")
      .replace(/\s+/g, " ")
      .trim()
      .replace(/^\p{Ll}/u, (letter) => letter.toUpperCase());
  }

  return raw;
}, [CANONICAL_TECHNIQUE_LABELS, dashboardT, getCanonicalLookupKey, i18n, t]);

const getArtworkTechnique = useCallback((row) => {
  const raw = String(row?.medium || row?.typeOfArt || "").trim();
  return raw || "Technique non renseignée";
}, []);

const getArtworkCategory = useCallback((row) => {
  const explicitCategory = String(row?.medium_category || row?.category || "").trim();
  const normalizedExplicit = normalizeTextForMatch(explicitCategory);
  const exactCategory = ARTWORK_CATEGORY_RULES.find((category) => normalizedExplicit === normalizeTextForMatch(category.key) || normalizedExplicit === normalizeTextForMatch(category.label));
  if (exactCategory) return exactCategory.label;

  const raw = `${explicitCategory} ${row?.typeOfArt || ""} ${row?.medium || ""}`;
  const normalized = normalizeTextForMatch(raw);
  const matched = ARTWORK_CATEGORY_RULES.find((category) => category.keywords.some((keyword) => normalized.includes(normalizeTextForMatch(keyword))));
  return matched?.label || "Autres catégories";
}, [ARTWORK_CATEGORY_RULES, normalizeTextForMatch]);

const getArtworkTrackedValue = useCallback((row) => {
  const value = Number(row?.valuation_market_value ?? row?.valuation_consensus ?? row?.current_estimated_value ?? row?.estimated_value ?? row?.value ?? 0);
  return Number.isFinite(value) ? value : 0;
}, []);

const computePortfolioSummary = useCallback((artworks = [], clientProfiles = {}, collectionsById = {}) => {
  const summarizeRows = (rows = [], clientId = "all", label = null, scope = "client", selectedCollection = null) => {
    const filtered = selectedCollection && selectedCollection !== "all"
      ? rows.filter((r) => getArtworkCollectionId(r) === selectedCollection)
      : rows;

    const totalArtworks = filtered.length;
    const valuedRows = filtered.filter((r) => {
      const val = Number(r?.valuation_market_value ?? r?.valuation_consensus ?? r?.current_estimated_value ?? r?.estimated_value ?? r?.value ?? 0);
      return val > 0;
    });
    const valuedArtworks = valuedRows.length;
    const totalValue = filtered.reduce((sum, r) => {
      const val = Number(r?.valuation_market_value ?? r?.valuation_consensus ?? r?.current_estimated_value ?? r?.estimated_value ?? r?.value ?? 0);
      return sum + (Number.isFinite(val) ? val : 0);
    }, 0);
    const valuationCoverage = totalArtworks > 0 ? Math.round((valuedArtworks / totalArtworks) * 100) : 0;


const hasDocumentSignal = (r) => {
  const docs =
    (typeof r?.documents === "string" && r.documents.trim().length > 0) ||
    (Array.isArray(r?.images) && r.images.length > 0) ||
    (Array.isArray(r?.documents) && r.documents.length > 0);

  return Boolean(
    r?.certificate_auth ||
    r?.certificate_blockchain ||
    r?.certificate_authenticity ||
    r?.condition_report ||
    r?.condition_report_url ||
    r?.dossier_generated ||
    docs
  );
};

const documentedCount = filtered.filter(hasDocumentSignal).length;
const documentationCoverage = totalArtworks > 0 ? Math.round((documentedCount / totalArtworks) * 100) : 0;

const liquidityScores = filtered
  .map((r) => computeLiquidityScore(r)?.score)
  .filter((score) => Number.isFinite(score));
 
const liquidRows = liquidityScores.filter((score) => score >= 60);

const liquidityCoverage =
  totalArtworks > 0 ? Math.round((liquidRows.length / totalArtworks) * 100) : 0;

const liquidityAverage =
  liquidityScores.length > 0
    ? Math.round(liquidityScores.reduce((sum, score) => sum + score, 0) / liquidityScores.length)
    : 0;

const liquidityLevel =
  liquidityAverage >= 70
    ? "Haute"
    : liquidityAverage >= 40
      ? "Moyenne"
      : "Limitée";

    let confidenceTotal = 0;
    let confidenceCount = 0;
    filtered.forEach((r) => {
      const c = getArtworkConfidence(r);
      if (c != null) { confidenceTotal += c; confidenceCount++; }
    });

    const aiRowsCount = filtered.filter((r) => artworkUsesAI(r)).length;

const artistMap = {};

filtered.forEach((r) => {
  const rawName = (r?.artist || "Inconnu").trim();
  const key = rawName
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();

  const val = Number(
    r?.valuation_market_value ??
    r?.valuation_consensus ??
    r?.current_estimated_value ??
    r?.estimated_value ??
    r?.value ??
    0
  );

  if (!artistMap[key]) {
    artistMap[key] = {
      name: rawName,
      value: 0,
      count: 0,
    };
  }

  artistMap[key].value += Number.isFinite(val) ? val : 0;
  artistMap[key].count += 1;
});

const distinctArtists = Object.values(artistMap).filter(
  (item) => item.name !== "Inconnu"
).length;

const sortedArtists = Object.values(artistMap).sort((a, b) => b.value - a.value);

const topArtists = sortedArtists.slice(0, 10).map((artist) => ({
  name: artist.name,
  value: artist.value,
  count: artist.count,
  share: totalValue > 0 ? Math.round((artist.value / totalValue) * 100) : 0,
}));
const topArtistsByCount = Object.values(artistMap)
  .sort((a, b) => b.count - a.count)
  .slice(0, 10)
  .map((artist) => ({
    name: artist.name,
    count: artist.count,
    value: artist.value,
    share: totalArtworks > 0
      ? Math.round((artist.count / totalArtworks) * 100)
      : 0,
  }));

const top3Value = sortedArtists.slice(0, 3).reduce((s, item) => s + item.value, 0);
const artistConcentration = totalValue > 0 ? Math.round((top3Value / totalValue) * 100) : 0;


    const techniqueMap = {};
    const categoryMap = {};

    filtered.forEach((r) => {
      const techniqueName = getArtworkTechnique(r);
      const techniqueKey = normalizeTextForMatch(techniqueName) || "technique non renseignée";
      const artworkValue = getArtworkTrackedValue(r);
      const categoryName = getArtworkCategory(r);

      if (!techniqueMap[techniqueKey]) {
        techniqueMap[techniqueKey] = { name: techniqueName, count: 0, value: 0 };
      }
      techniqueMap[techniqueKey].count += 1;
      techniqueMap[techniqueKey].value += artworkValue;

      if (!categoryMap[categoryName]) {
        categoryMap[categoryName] = { name: categoryName, count: 0, value: 0 };
      }
      categoryMap[categoryName].count += 1;
      categoryMap[categoryName].value += artworkValue;
    });

    const distinctTechniques = Object.keys(techniqueMap).length;
    const distinctCategories = Object.keys(categoryMap).length;

    const topTechniques = Object.values(techniqueMap)
      .sort((a, b) => b.count - a.count || b.value - a.value)
      .slice(0, 6)
      .map((item) => ({
        ...item,
        share: totalArtworks > 0 ? Math.round((item.count / totalArtworks) * 100) : 0,
        valueShare: totalValue > 0 ? Math.round((item.value / totalValue) * 100) : 0,
      }));

    const categoryDistribution = Object.values(categoryMap)
      .sort((a, b) => b.count - a.count || b.value - a.value)
      .map((item) => ({
        ...item,
        share: totalArtworks > 0 ? Math.round((item.count / totalArtworks) * 100) : 0,
        valueShare: totalValue > 0 ? Math.round((item.value / totalValue) * 100) : 0,
        deltaShare: totalValue > 0 && totalArtworks > 0
          ? Math.round((item.value / totalValue) * 100) - Math.round((item.count / totalArtworks) * 100)
          : 0,
      }));

    const categoryValueDistribution = [...categoryDistribution].sort((a, b) => b.value - a.value);
    const dominantCategory = categoryDistribution[0] || null;
    const valueDominantCategory = categoryValueDistribution[0] || null;
    const categoryAllocationInsight = valueDominantCategory
      ? `${valueDominantCategory.name} représente ${valueDominantCategory.valueShare}% de la valeur suivie pour ${valueDominantCategory.share}% des œuvres.`
      : "Ajoutez des valeurs pour lire le poids économique par catégorie.";

    const technicalDiversityLabel = distinctTechniques >= 30
      ? "Collection très diversifiée"
      : distinctTechniques >= 10
        ? "Collection diversifiée"
        : "Diversité technique concentrée";

    const collectionMap = {};
    filtered.forEach((r) => {
      const colId = getArtworkCollectionId(r) || "unknown";
      if (!collectionMap[colId]) collectionMap[colId] = [];
      collectionMap[colId].push(r);
    });
    const collectionSummaries = Object.entries(collectionMap).map(([colId, colRows]) => {
      const col = collectionsById[colId];
      return {
        collectionId: colId,
        collectionName: col?.title || col?.name || "Collection",
        totalArtworks: colRows.length,
        totalValue: colRows.reduce((s, r) => {
          const v = Number(r?.valuation_market_value ?? r?.valuation_consensus ?? r?.current_estimated_value ?? r?.estimated_value ?? r?.value ?? 0);
          return s + (Number.isFinite(v) ? v : 0);
        }, 0),
      };
    });

    const isPortfolioReady = totalValue >= 150000 && totalArtworks >= 12 && distinctArtists >= 3 && distinctCategories >= 2 && valuationCoverage >= 60;
    const isPatrimonialReady = totalValue >= 500000 && totalArtworks >= 25;
    const readinessMissing = [];
    if (totalValue < 150000) readinessMissing.push("Valeur suivie < 150 000 €");
    if (totalArtworks < 12) readinessMissing.push("Moins de 12 œuvres");
    if (distinctArtists < 3) readinessMissing.push("Moins de 3 artistes");
    if (distinctCategories < 2) readinessMissing.push("Moins de 2 catégories");
    if (valuationCoverage < 60) readinessMissing.push("Couverture valorisation < 60%");

    let maturityLevel, maturityLabel;
    if (isPatrimonialReady) { maturityLevel = 4; maturityLabel = "Patrimoine fort"; }
    else if (isPortfolioReady) { maturityLevel = 3; maturityLabel = "Portefeuille structuré"; }
    else if (totalArtworks >= 5 && distinctArtists >= 2) { maturityLevel = 2; maturityLabel = "Collection structurée"; }
    else { maturityLevel = 1; maturityLabel = "Collection en construction"; }

    return {
      clientId,
      clientName: label || (clientId === "all" ? "Vue consolidée" : (clientProfiles[clientId]?.displayName || "Client")),
      totalArtworks, totalValue, valuedArtworks, valuationCoverage,
      documentationCoverage, liquidityCoverage, liquidityAverage, liquidityLevel,
      averageConfidence: confidenceCount > 0 ? Math.round(confidenceTotal / confidenceCount) : null,
      
      artistConcentration, topArtists, topArtistsByCount, topTechniques, categoryDistribution, categoryValueDistribution, dominantCategory, valueDominantCategory, categoryAllocationInsight, technicalDiversityLabel, collectionSummaries,
      distinctArtists, distinctTechniques, distinctCategories, maturityLevel, maturityLabel,
      isPortfolioReady, isPatrimonialReady, readinessMissing,
      usesAI: aiRowsCount > 0,
riskFlags: [
  totalArtworks > 0 && valuationCoverage < 60 ? `Valorisations à compléter (${100 - valuationCoverage}% des œuvres sans base)` : null,

  totalArtworks > 0 && documentationCoverage < 80
    ? `Documentation à renforcer (${100 - documentationCoverage}% des œuvres sans dossier documentaire complet)`
    : null,

  distinctTechniques > Math.max(20, Math.round(totalArtworks * 0.12))
    ? `Techniques trop dispersées (${distinctTechniques} techniques pour ${totalArtworks} œuvres) : normalisation à vérifier`
    : null,

  totalValue > 0 && artistConcentration > 40 ? `Diversification à renforcer (${artistConcentration}% concentrés sur ${Math.min(3, distinctArtists)} artistes)` : null,
  totalArtworks > 0 && distinctArtists < 3 ? "Diversification limitée (moins de 3 artistes)" : null,
].filter(Boolean),

    };
  };

  const safeRows = Array.isArray(artworks) ? artworks : [];
  const byClientRows = new Map();
  safeRows.forEach((row) => {
    const clientId = row?.client_id || "unknown";
    if (!byClientRows.has(clientId)) byClientRows.set(clientId, []);
    byClientRows.get(clientId).push(row);
  });

  const clientSummaries = [...byClientRows.entries()]
    .map(([clientId, rows]) => summarizeRows(rows, clientId))
    .sort((a, b) => {
      if (Number(b.isPortfolioReady) !== Number(a.isPortfolioReady)) return Number(b.isPortfolioReady) - Number(a.isPortfolioReady);
      if (b.totalValue !== a.totalValue) return b.totalValue - a.totalValue;
      return b.totalArtworks - a.totalArtworks;
    });

  const dominantClient = clientSummaries[0] || null;
  const globalSummary = summarizeRows(safeRows, "all", "Vue consolidée", "global");
  globalSummary.clientConcentration = globalSummary.totalValue > 0 && dominantClient
    ? Math.round((dominantClient.totalValue / globalSummary.totalValue) * 100)
    : 0;
  globalSummary.clientSummaries = clientSummaries;
  globalSummary.dominantClient = dominantClient;

  return globalSummary;
}, [getArtworkCollectionId, getArtworkConfidence, artworkUsesAI, computeLiquidityScore, getArtworkTechnique, normalizeTextForMatch, getArtworkTrackedValue, getArtworkCategory]);

const loadPortfolioSummary = useCallback(async () => {
  if (!user?.id) {
    const emptySummary = computePortfolioSummary([], {}, {});
    emptySummary.rawArtworks = [];
    emptySummary.collectionsById = {};
    setPortfolioSummary(emptySummary);
    setSelectedPortfolioClientId("auto");
    setSelectedPortfolioCollectionId("all");
    return;
  }

  setLoadingPortfolio(true);

  try {
    const { data: perms, error: permsErr } = await supabase
      .from("collection_permissions")
      .select("client_id, collection_id, can_view, can_edit")
      .eq("grantee_id", user.id);

    if (permsErr) throw permsErr;

    const permissionRows = Array.isArray(perms) ? perms : [];

    const accessiblePerms = permissionRows.filter(
      (p) =>
        p?.collection_id &&
        (
          p.can_view === true ||
          p.can_edit === true ||
          (p.can_view == null && p.can_edit == null)
        )
    );
    const sharedCollectionIds = [
      ...new Set(accessiblePerms.map((p) => p.collection_id).filter(Boolean)),
    ];

    // Charger en parallèle : collections partagées + collections propres de l'expert
    const [sharedResult, ownResult] = await Promise.all([
      sharedCollectionIds.length > 0
        ? supabase.from("collections").select("id, client_id, title, updated_at").in("id", sharedCollectionIds)
        : Promise.resolve({ data: [], error: null }),
      supabase.from("collections").select("id, client_id, title, updated_at").eq("client_id", user.id),
    ]);

    if (sharedResult.error) throw sharedResult.error;
    if (ownResult.error) throw ownResult.error;

    const ownIds = new Set((ownResult.data || []).map((c) => c.id));
    const mergedCollections = [
      ...(ownResult.data || []),
      ...(sharedResult.data || []).filter((c) => !ownIds.has(c.id)),
    ];
    const collectionIds = mergedCollections.map((c) => c.id);

    console.log("[portfolio] perms", permissionRows);
    console.log("[portfolio] collectionIds", collectionIds);

    if (collectionIds.length === 0) {
      const emptySummary = computePortfolioSummary([], {}, {});
      emptySummary.rawArtworks = [];
      emptySummary.collectionsById = {};
      setPortfolioSummary(emptySummary);
      setSelectedPortfolioClientId("auto");
      setSelectedPortfolioCollectionId("all");
      return;
    }

    const collections = mergedCollections;

    const safeCollections = Array.isArray(collections) ? collections : [];

    const collectionsById = Object.fromEntries(
      safeCollections.map((col) => [
        col.id,
        {
          ...col,
          name: col.title || col.name || dashboardT("complete.portfolio.collectionFallback"),
        },
      ])
    );

    const clientIds = [
      ...new Set(safeCollections.map((col) => col.client_id).filter(Boolean)),
    ];

    let clientProfiles = {};
    if (clientIds.length > 0) {
      const { data: profs, error: profErr } = await supabase.rpc(
        "get_profile_basics",
        { p_user_ids: clientIds }
      );

      if (!profErr && Array.isArray(profs)) {
        clientProfiles = Object.fromEntries(
          profs.map((p) => [
            p.id,
            {
              displayName:
                `${p.first_name || ""} ${p.last_name || ""}`.trim() ||
                p.company ||
                dashboardT("complete.portfolio.clientWithId", { id: String(p.id).slice(0, 8) }),
            },
          ])
        );
      }
    }

const { data: artworks, error: artworksErr } = await supabase
  .from("artworks_secure")
  .select(`
    id,
    client_id,
    collection_id,
    artist,
    medium,
    typeOfArt,
    value,
    estimated_value,
    valuation_market_value,
    valuation_consensus,
    current_estimated_value,
    valuation_confidence_score,
    visual_analysis,
    style_ai,
    certificate_auth,
    certificate_blockchain,
    certificate_authenticity,
    condition_report,
    condition_report_url,
    dossier_generated,
    documents,
    images,
    market_liquidity,
    market_demand,
    market_trend
  `)
  .in("collection_id", collectionIds);

    if (artworksErr) throw artworksErr;

    const rawArtworks = Array.isArray(artworks) ? artworks : [];
    const safeArtworks = rawArtworks.map((row) => {
      if (row.client_id) return row;
      const col = collectionsById[row.collection_id];
      return col?.client_id ? { ...row, client_id: col.client_id } : row;
    });

    console.log("[portfolio] collections", safeCollections);
    console.log("[portfolio] raw artworks count", rawArtworks.length);
    console.log("[portfolio] raw artworks sample", rawArtworks.slice(0, 5));

    const summary = computePortfolioSummary(
      safeArtworks,
      clientProfiles,
      collectionsById
    );

    summary.rawArtworks = safeArtworks;
    summary.collectionsById = collectionsById;

    console.log("[portfolio] summary", summary);

    setPortfolioSummary(summary);
    setSelectedPortfolioClientId((prev) => {
      if (prev === "auto") return prev;
      const exists = summary.clientSummaries?.some(
        (item) => item.clientId === prev
      );
      return exists ? prev : "auto";
    });
    setSelectedPortfolioCollectionId("all");
  } catch (err) {
    console.warn("[expert] loadPortfolioSummary failed", err);
    const emptySummary = computePortfolioSummary([], {}, {});
    emptySummary.rawArtworks = [];
    emptySummary.collectionsById = {};
    setPortfolioSummary(emptySummary);
    setSelectedPortfolioClientId("auto");
    setSelectedPortfolioCollectionId("all");
  } finally {
    setLoadingPortfolio(false);
  }
}, [computePortfolioSummary, user?.id, dashboardT]);

  const loadProfileBasics = useCallback(async () => {
    if (!user?.id) return;
    try {
      const { data, error } = await supabase
        .from("profiles")
        .select("id, first_name, last_name, plan, advisor_title, advisor_logo_url, advisor_logo_public_url")
        .eq("id", user.id)
        .maybeSingle();
      if (error) throw error;
      setProfileBasics(data || null);
    } catch (e) {
      console.warn("[expert] loadProfileBasics failed", e);
      setProfileBasics(null);
    }
  }, [user?.id]);

  const loadClientInvites = useCallback(async () => {
    if (!user?.id) return;
    try {
      const { data, error } = await supabase
        .from("expert_clients")
        .select("id, status, invited_at, message, client_id, origin, introduction_fee_status")
        .eq("expert_id", user.id)
        .eq("status", "pending")
        .order("invited_at", { ascending: false });

      if (error) throw error;

      let rows = Array.isArray(data) ? data : [];
      const clientIds = [...new Set(rows.map((r) => r.client_id).filter(Boolean))];

      let byId = {};
      if (clientIds.length) {
        const { data: profs, error: perr } = await supabase.rpc("get_profile_basics", {
          p_user_ids: clientIds,
        });
        if (!perr && Array.isArray(profs)) {
          byId = Object.fromEntries(profs.map((p) => [p.id, p]));
        }
      }

      const mapped = rows.map((r) => ({
        id: r.id,
        status: r.status,
        invited_at: r.invited_at || null,
        message: r.message || null,
        client_id: r.client_id,
        origin: r.origin || "expert_invite",
        introduction_fee_status: r.introduction_fee_status || "none",
        client_profile: byId[r.client_id] || null,
      }));

      setClientInvites(mapped);
      setClientInvitesCount(mapped.length || 0);
    } catch (e) {
      console.error("[ExpertDashboard] loadClientInvites failed", e);
      setClientInvites([]);
      setClientInvitesCount(0);
    }
  }, [user?.id]);

  // ------------------------------------------------------------
  // Helpers manquants (invites + counts + profil expert)
  // ------------------------------------------------------------
  const loadInvites = useCallback(async () => {
    if (!user?.id) return;
    try {
      const { data, error } = await supabase
        .from("advisor_invites")
        .select("*")
        .eq("advisor_id", user.id)
        .order("created_at", { ascending: false });

      if (error) throw error;

      setInvites(data || []);
      setInvitesCount((data || []).length || 0);
    } catch (e) {
      console.warn("[ExpertDashboard] loadInvites failed", e);
      setInvites([]);
      setInvitesCount(0);
    }
  }, [user?.id]);

  const loadIncomingClientInvitesCount = useCallback(async () => {
    if (!user?.id) return 0;
    try {
      const { count, error } = await supabase
        .from("expert_clients")
        .select("id", { count: "exact", head: true })
        .eq("expert_id", user.id)
        .eq("status", "pending");

      if (error) throw error;
      const safe = count || 0;
      setIncomingClientInvitesCount(safe);
      return safe;
    } catch (e) {
      console.warn("[ExpertDashboard] loadIncomingClientInvitesCount failed", e);
      setIncomingClientInvitesCount(0);
      return 0;
    }
  }, [user?.id]);

  const getFallbackName = useCallback(() => {
    const fn = (profileBasics?.first_name || "").trim();
    const ln = (profileBasics?.last_name || "").trim();
    const full = `${fn} ${ln}`.trim();
    return full || dashboardT("complete.common.expert");
  }, [profileBasics, dashboardT]);

  const loadExpertProfile = useCallback(async () => {
    if (!user?.id) return;
    setLoadingExpert(true);
    try {
      const { data, error } = await supabase
        .from("experts")
        .select("*")
        .eq("expert_user_id", user.id)
        .order("created_at", { ascending: false })
        .limit(1);

      if (error) throw error;

      const row = Array.isArray(data) && data.length ? data[0] : null;
      setExpertRow(row);

      if (row) {
        setProfileForm((prev) => ({
          ...prev,
          is_public: !!row.is_public,
          name: row.name ?? prev.name ?? "",
          specialty: row.specialty ?? prev.specialty ?? "",
          circle_kind: row.circle_kind ?? prev.circle_kind ?? "",
          bio: row.bio ?? prev.bio ?? "",
          organization: row.organization ?? prev.organization ?? "",
          location: row.location ?? prev.location ?? "",
          contact_email: row.contact_email ?? prev.contact_email ?? "",
          contact_phone: row.contact_phone ?? prev.contact_phone ?? "",
          phone: row.phone ?? prev.phone ?? "",
          experience: row.experience ?? prev.experience ?? "",
          credentials: row.credentials ?? prev.credentials ?? "",
          specializations: row.specializations ?? prev.specializations ?? "",
          languages: row.languages ?? prev.languages ?? "",
          image_url: row.image_url ?? prev.image_url ?? "",
        }));
      }
    } catch (e) {
      console.error("[ExpertDashboard] loadExpertProfile failed", e);
      setExpertRow(null);
    } finally {
      setLoadingExpert(false);
    }
  }, [user?.id]);

  const upsertExpertProfile = useCallback(async () => {
    if (!user?.id) return;
    setSavingExpert(true);
    try {
      const fallbackName = getFallbackName();
      const safeName = (profileForm.name || "").trim() || fallbackName;

      const payload = {
        expert_user_id: user.id,
        is_public: !!profileForm.is_public,
        name: safeName,
        specialty: (profileForm.specialty || "").trim() || null,
        circle_kind: profileForm.circle_kind || null,
        bio: (profileForm.bio || "").trim() || null,
        organization: (profileForm.organization || "").trim() || null,
        location: (profileForm.location || "").trim() || null,
        contact_email: (profileForm.contact_email || "").trim() || null,
        contact_phone: (profileForm.contact_phone || "").trim() || null,
        phone: (profileForm.phone || "").trim() || null,
        experience: (profileForm.experience || "").trim() || null,
        credentials: (profileForm.credentials || "").trim() || null,
        specializations: (profileForm.specializations || "").trim() || null,
        languages: (profileForm.languages || "").trim() || null,
        image_url: (profileForm.image_url || "").trim() || null,
        updated_at: new Date().toISOString(),
      };

      const { error } = await supabase.from("experts").upsert(payload, { onConflict: "expert_user_id" });
      if (error) throw error;

      toast.success(dashboardT("complete.profile.updated"));
      await loadExpertProfile();
    } catch (e) {
      console.error("[ExpertDashboard] upsertExpertProfile failed", e);
      toast.error(dashboardT("complete.profile.saveError"));
    } finally {
      setSavingExpert(false);
    }
  }, [user?.id, profileForm, getFallbackName, loadExpertProfile, dashboardT]);

  const togglePublicPresence = useCallback(
    async (nextValue) => {
      if (!user?.id) return;
      try {
        await supabase
          .from("experts")
          .upsert(
            {
              expert_user_id: user.id,
              is_public: !!nextValue,
              updated_at: new Date().toISOString(),
            },
            { onConflict: "expert_user_id" }
          );

        setProfileForm((p) => ({ ...p, is_public: !!nextValue }));
        await loadExpertProfile();
      } catch (e) {
        console.error("[ExpertDashboard] togglePublicPresence failed", e);
        toast.error(dashboardT("complete.profile.visibilityError"));
      }
    },
    [user?.id, loadExpertProfile, dashboardT]
  );

  const uploadExpertPhoto = useCallback(
    async (file) => {
      if (!user?.id || !file) return;
      setUploadingPhoto(true);
      try {
        await upsertExpertProfile();

        const ext = (file.name.split(".").pop() || "jpg").toLowerCase();
        const filePath = `experts/${user.id}/photo_${Date.now()}.${ext}`;

        const { error: upErr } = await supabase.storage.from("experts").upload(filePath, file, { upsert: true });
        if (upErr) throw upErr;

        const { data: pub } = supabase.storage.from("experts").getPublicUrl(filePath);
        const publicUrl = pub?.publicUrl || null;

        await supabase
          .from("experts")
          .upsert({ expert_user_id: user.id, image_url: publicUrl, updated_at: new Date().toISOString() }, { onConflict: "expert_user_id" });

        setProfileForm((p) => ({ ...p, image_url: publicUrl || "" }));
        await loadExpertProfile();
        toast.success(dashboardT("complete.profile.photoUpdated"));
      } catch (e) {
        console.error("[ExpertDashboard] uploadExpertPhoto failed", e);
        toast.error(dashboardT("complete.profile.photoUploadError"));
      } finally {
        setUploadingPhoto(false);
      }
    },
    [user?.id, upsertExpertProfile, loadExpertProfile, dashboardT]
  );

  const uploadCvAndScan = useCallback(
    async (file) => {
      if (!user?.id || !file) return;
      setUploadingCv(true);
      try {
        await upsertExpertProfile();

        const ext = (file.name.split(".").pop() || "pdf").toLowerCase();
        const filePath = `experts/${user.id}/cv_${Date.now()}.${ext}`;

        const { error: upErr } = await supabase.storage.from("experts").upload(filePath, file, { upsert: true });
        if (upErr) throw upErr;

        toast.success(dashboardT("complete.profile.cvUploaded"));
      } catch (e) {
        console.error("[ExpertDashboard] uploadCvAndScan failed", e);
        toast.error(dashboardT("complete.profile.cvUploadError"));
      } finally {
        setUploadingCv(false);
      }
    },
    [user?.id, upsertExpertProfile, dashboardT]
  );

  // ✅ ACCEPT: expert_clients.accepted + sync Kanban => expert_service_requests.sharingwaiting
  const acceptClientInvite = useCallback(
    async (inviteId, opts = {}) => {
      if (!user?.id || !inviteId) return;
      // Collectionneur venu de la plateforme + expert non abonné : mise en relation payante (49 € HT).
      const invite = clientInvites.find((i) => i.id === inviteId);
      if (!opts.skipFeeCheck && invite?.origin === "platform_client" && !introIncluded) {
        setIntroModal({ inviteId });
        return;
      }
      try {
        const now = new Date().toISOString();

        const { data: inviteRow, error: inviteLoadErr } = await supabase
          .from("expert_clients")
          .select("id, client_id, expert_id")
          .eq("id", inviteId)
          .eq("expert_id", user.id)
          .maybeSingle();

        if (inviteLoadErr) throw inviteLoadErr;

        const { error } = await supabase
          .from("expert_clients")
          .update({ status: "accepted", accepted_at: now, declined_at: null })
          .eq("id", inviteId)
          .eq("expert_id", user.id);
        if (error) throw error;

        if (inviteRow?.client_id) {
          const { error: srSyncErr } = await supabase
            .from("expert_service_requests")
            .update({ status: "sharingwaiting", updated_at: now })
            .eq("client_id", inviteRow.client_id)
            .eq("expert_id", user.id)
            .contains("metadata", { expert_client_id: inviteId });

          if (srSyncErr) console.warn("[ExpertDashboard] Kanban sync (accept) failed", srSyncErr);
        }

        toast.success(t("expert.Dashboard.clientInvites.accepted"));
        await loadClientInvites();
        await loadIncomingClientInvitesCount();
      } catch (e) {
        console.error("[ExpertDashboard] acceptClientInvite failed", e);
        // Garde serveur : la base refuse l'acceptation tant que les frais ne sont pas réglés.
        if (String(e?.message || "").includes("INTRODUCTION_FEE_REQUIRED")) {
          setIntroModal({ inviteId });
          return;
        }
        toast.error(t("expert.Dashboard.clientInvites.acceptError"));
      }
    },
    [user?.id, t, clientInvites, introIncluded, loadClientInvites, loadIncomingClientInvitesCount]
  );

  // Lance le paiement par carte (Stripe Checkout) de la mise en relation.
  const startIntroPayment = useCallback(
    async (inviteId) => {
      if (!inviteId) return;
      setIntroBusy(true);
      try {
        const { data, error } = await supabase.functions.invoke("expert-introduction", {
          body: { action: "checkout", expert_client_id: inviteId, return_origin: window.location.origin },
        });
        if (error || data?.error) throw new Error(data?.error || error?.message || "checkout_error");

        if (data?.url) {
          window.location.href = data.url;
          return; // redirection : on garde le bouton bloqué
        }
        setIntroModal(null);
        if (data?.accepted) {
          toast.success(t("expert.Dashboard.clientInvites.introFee.paidAccepted"));
          await loadClientInvites();
          await loadIncomingClientInvitesCount();
        } else if (data?.included) {
          // Abonné ou client propre : acceptation directe (le trigger valide).
          await acceptClientInvite(inviteId, { skipFeeCheck: true });
        }
      } catch (e) {
        console.error("[ExpertDashboard] startIntroPayment failed", e);
        toast.error(t("expert.Dashboard.clientInvites.introFee.error"));
      } finally {
        setIntroBusy(false);
      }
    },
    [t, acceptClientInvite, loadClientInvites, loadIncomingClientInvitesCount]
  );

  // Vérifie auprès du serveur (qui interroge Stripe) que la mise en relation est payée, puis l'accepte.
  const confirmIntroPayment = useCallback(
    async (inviteId, { silent = false } = {}) => {
      if (!inviteId) return false;
      try {
        const { data, error } = await supabase.functions.invoke("expert-introduction", {
          body: { action: "confirm", expert_client_id: inviteId },
        });
        if (error || data?.error) throw new Error(data?.error || error?.message || "confirm_error");
        if (data?.accepted) {
          toast.success(t("expert.Dashboard.clientInvites.introFee.paidAccepted"));
          await loadClientInvites();
          await loadIncomingClientInvitesCount();
          return true;
        }
        if (!silent) toast.info(t("expert.Dashboard.clientInvites.introFee.notPaidYet"));
      } catch (e) {
        console.error("[ExpertDashboard] confirmIntroPayment failed", e);
        if (!silent) toast.error(t("expert.Dashboard.clientInvites.introFee.confirmError"));
      }
      return false;
    },
    [t, loadClientInvites, loadIncomingClientInvitesCount]
  );

  // Retour de Stripe Checkout (?ec=…&intro_session=…) : confirmation serveur puis nettoyage de l'URL.
  const introReturnHandled = useRef(false);
  useEffect(() => {
    if (!user?.id || introReturnHandled.current) return;
    const ec = searchParams.get("ec");
    const sid = searchParams.get("intro_session");
    const canceled = searchParams.get("intro_canceled");
    const clean = () =>
      setSearchParams(
        (prev) => {
          const p = new URLSearchParams(prev);
          p.delete("ec");
          p.delete("intro_session");
          p.delete("intro_canceled");
          return p;
        },
        { replace: true }
      );
    if (ec && sid) {
      introReturnHandled.current = true;
      confirmIntroPayment(ec).finally(clean);
    } else if (canceled) {
      introReturnHandled.current = true;
      toast.info(t("expert.Dashboard.clientInvites.introFee.canceled"));
      clean();
    }
  }, [user?.id, searchParams, setSearchParams, confirmIntroPayment, t]);

  // ✅ REJECT: expert_clients.declined + sync Kanban => expert_service_requests.cancelled
  const rejectClientInvite = useCallback(
    async (inviteId) => {
      if (!user?.id || !inviteId) return;
      try {
        const now = new Date().toISOString();

        const { data: inviteRow, error: inviteLoadErr } = await supabase
          .from("expert_clients")
          .select("id, client_id, expert_id")
          .eq("id", inviteId)
          .eq("expert_id", user.id)
          .maybeSingle();

        if (inviteLoadErr) throw inviteLoadErr;

        const { error } = await supabase
          .from("expert_clients")
          .update({ status: "declined", declined_at: now, accepted_at: null })
          .eq("id", inviteId)
          .eq("expert_id", user.id);
        if (error) throw error;

        if (inviteRow?.client_id) {
          const { error: srSyncErr } = await supabase
            .from("expert_service_requests")
            .update({ status: "cancelled", updated_at: now })
            .eq("client_id", inviteRow.client_id)
            .eq("expert_id", user.id)
            .contains("metadata", { expert_client_id: inviteId });

          if (srSyncErr) console.warn("[ExpertDashboard] Kanban sync (decline) failed", srSyncErr);
        }

        toast.success(t("expert.Dashboard.clientInvites.declined"));
        await loadClientInvites();
        await loadIncomingClientInvitesCount();
      } catch (e) {
        console.error("[ExpertDashboard] rejectClientInvite failed", e);
        toast.error(t("expert.Dashboard.clientInvites.declineError"));
      }
    },
    [user?.id, t, loadClientInvites, loadIncomingClientInvitesCount]
  );

  const createInvite = useCallback(async () => {
    if (!user?.id) return;
    const email = (inviteEmail || "").trim();
    if (!emailRegex.test(email)) {
      alert(t("expert.Dashboard.invites.emailInvalid"));
      return;
    }
    setSendingInvite(true);
    try {
      const token = genToken();
      const expires = new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString();
      const { error } = await supabase
        .from("advisor_invites")
        .insert([
          {
            advisor_id: user.id,
            email,
            note: (inviteNote || "").trim() || null,
            token,
            status: "pending",
            expires_at: expires,
          },
        ]);
      if (error) throw error;

      setShowInvite(false);
      setInviteEmail("");
      setInviteNote("");
      await loadInvites();
    } catch (e) {
      console.error("[invites] createInvite", e);
      alert(t("expert.Dashboard.invites.error"));
    } finally {
      setSendingInvite(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, inviteEmail, inviteNote, loadInvites, t]);

  const loadServiceRequests = useCallback(async () => {
    if (!user?.id) return;
    setLoadingServices(true);
    setErrorMsg("");
    try {
      const { data, error } = await supabase
        .from("expert_service_requests")
        .select("*")
        .eq("expert_id", user.id)
        .order("created_at", { ascending: false });
      if (error) throw error;

      let rows = Array.isArray(data) ? data : [];
      const clientIds = [...new Set(rows.map((r) => r.client_id).filter(Boolean))];
      if (clientIds.length) {
        const { data: profs, error: perr } = await supabase.rpc("get_profile_basics", { p_user_ids: clientIds });
        if (!perr && Array.isArray(profs)) {
          const byId = Object.fromEntries(profs.map((p) => [p.id, p]));
          rows = rows.map((r) => ({ ...r, profiles: byId[r.client_id] || null }));
        }
      }

      setServiceRequests(rows);
if (typeof refreshUnreadCount === "function") {
  refreshUnreadCount();
}
    } catch (e) {
      console.error("[services] loadServiceRequests", e);
      setErrorMsg(t("expert.Dashboard.services.errorMessage"));
      toast.error(t("expert.Dashboard.services.errorToast"));
    } finally {
      setLoadingServices(false);
    }
  }, [user?.id, t, refreshUnreadCount]);

  const startRequest = useCallback(async (id) => {
    if (!user?.id) return;
    await supabase
      .from("expert_service_requests")
      .update({ status: "in_progress", started_at: new Date().toISOString() })
      .eq("id", id)
      .eq("expert_id", user.id);
    await loadServiceRequests();
    toast.success(t("expert.Dashboard.services.toasts.taken"));
  }, [user?.id, loadServiceRequests, t]);

  const markReflection = useCallback(async (id) => {
    if (!user?.id) {
      toast.error(dashboardT("complete.common.userNotLoggedIn"));
      return;
    }
    try {
      const { error } = await supabase
        .from("expert_service_requests")
        .update({ status: "awaiting_feedback" })
        .eq("id", id)
        .eq("expert_id", user.id);

      if (error) {
        console.error("Erreur markReflection:", error);
        toast.error(dashboardT("complete.common.errorWithMessage", { message: error.message }));
        return;
      }

      await loadServiceRequests();
      toast.success(dashboardT("complete.services.submittedForReview"));
    } catch (err) {
      console.error("Exception markReflection:", err);
      toast.error(dashboardT("complete.common.errorWithMessage", { message: err.message }));
    }
  }, [user?.id, loadServiceRequests, dashboardT]);

  const acceptClient = useCallback(async (request) => {
    if (!user?.id) {
      toast.error(dashboardT("complete.common.userNotLoggedIn"));
      return;
    }

    setShowAcceptModal(false);
    setRequestToAccept(null);

    const toastId = toast.loading(dashboardT("complete.services.acceptLoading"));

    try {
      const { error: updateError } = await supabase
        .from("expert_service_requests")
        .update({
          status: "completed",
          completed_at: new Date().toISOString(),
        })
        .eq("id", request.id)
        .eq("expert_id", user.id);

      if (updateError) throw updateError;

      await loadServiceRequests();

      toast.update(toastId, {
        render: dashboardT("complete.services.acceptedClosed"),
        type: "success",
        isLoading: false,
        autoClose: 5000,
      });
    } catch (err) {
      console.error("Exception acceptClient:", err);
      toast.update(toastId, {
        render: dashboardT("complete.common.errorWithMessage", { message: err.message }),
        type: "error",
        isLoading: false,
        autoClose: 3000,
      });
    }
  }, [user?.id, loadServiceRequests, dashboardT]);

  const rejectClient = useCallback(async (id) => {
    if (!user?.id) {
      toast.error(dashboardT("complete.common.userNotLoggedIn"));
      return;
    }

    if (!window.confirm(dashboardT("complete.services.rejectConfirm"))) return;

    const toastId = toast.loading(dashboardT("complete.services.rejectLoading"));

    try {
      const { error } = await supabase
        .from("expert_service_requests")
        .update({ status: "cancelled", cancelled_at: new Date().toISOString() })
        .eq("id", id)
        .eq("expert_id", user.id);

      if (error) throw error;

      await loadServiceRequests();

      toast.update(toastId, {
        render: dashboardT("complete.services.rejected"),
        type: "info",
        isLoading: false,
        autoClose: 3000,
      });
    } catch (err) {
      console.error("Exception rejectClient:", err);
      toast.update(toastId, {
        render: dashboardT("complete.common.errorWithMessage", { message: err.message }),
        type: "error",
        isLoading: false,
        autoClose: 3000,
      });
    }
  }, [user?.id, loadServiceRequests, dashboardT]);

  const openConversationForRequest = useCallback(async (request, subject) => {
    const toastId = toast.loading(dashboardT("complete.services.conversationOpening"));
    try {
      const { data, error } = await supabase.rpc("ensure_conversation", {
        p_type: "service_request",
        p_context_type: "expert_service_requests",
        p_context_id: request.id,
        p_subject: subject,
        p_other_user_id: request.client_id,
        p_other_role: "client",
      });

      if (error) throw error;
      if (!data) throw new Error("ensure_conversation returned null");

try {
  if (typeof markConversationAsRead === "function") {
    markConversationAsRead(data);
  }
} catch (_) {}

console.log("[useMessageNotifications]", {
  refreshUnreadCount,
  type_refresh: typeof refreshUnreadCount,
  markConversationAsRead,
  type_mark: typeof markConversationAsRead,
});
      toast.update(toastId, {
        render: dashboardT("complete.services.conversationOpened"),
        type: "success",
        isLoading: false,
        autoClose: 1200,
      });

      navigate(`/expert/messages?conversation=${data}`);
    } catch (e) {
      console.error("[messaging] open failed", e);
      toast.update(toastId, {
        render: dashboardT("complete.services.conversationOpenError"),
        type: "error",
        isLoading: false,
        autoClose: 3000,
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate, markConversationAsRead]);

  // initial load + realtime (inchangé)
  useEffect(() => {
    if (loaded.current || !user?.id) return;
    loaded.current = true;

    loadServiceRequests();
    loadInvites();
    loadIncomingClientInvitesCount();
    loadClientInvites();
    loadExpertProfile();
    loadProfileBasics();
    loadPortfolioSummary();

    (async () => {
      try {
        const { count } = await supabase
          .from("collection_permissions")
          .select("client_id", { count: "exact", head: true })
          .eq("grantee_id", user.id)
          .eq("can_view", true);
        setSharedCount(count || 0);
      } catch (_) {}
    })();

    const channel = supabase
      .channel("pr_expert_changes")
      .on("postgres_changes", { event: "*", schema: "public", table: "expert_service_requests" }, () => {
        loadServiceRequests();
      })
      .subscribe();

    return () => {
      try { supabase.removeChannel(channel); } catch (_) {}
    };
  }, [
    user?.id,
    loadServiceRequests,
    loadInvites,
    loadIncomingClientInvitesCount,
    loadClientInvites,
    loadProfileBasics,
    loadExpertProfile,
    loadPortfolioSummary,
  ]);

  const stats = {
    total: serviceRequests.length,
    pending: serviceRequests.filter((r) => (r.status ?? "pending") === "pending").length,
    in_progress: serviceRequests.filter((r) => r.status === "in_progress").length,
    awaiting_feedback: serviceRequests.filter((r) => r.status === "awaiting_feedback").length,
    completed: serviceRequests.filter((r) => r.status === "completed").length,
    cancelled: serviceRequests.filter((r) => r.status === "cancelled").length,
  };

const portfolioClientOptions = portfolioSummary?.clientSummaries || [];

const selectedPortfolioClientSummary = selectedPortfolioClientId === "auto"
  ? null
  : portfolioClientOptions.find((item) => item.clientId === selectedPortfolioClientId) || null;

const portfolioBaseFocus = selectedPortfolioClientSummary || portfolioSummary?.dominantClient || null;
const isPortfolioFocusedOnMajor = !selectedPortfolioClientSummary && Boolean(portfolioSummary?.dominantClient);

const portfolioCollectionOptions = useMemo(() => {
  if (!portfolioBaseFocus?.collectionSummaries) return [];
  return portfolioBaseFocus.collectionSummaries;
}, [portfolioBaseFocus]);

useEffect(() => {
  setSelectedPortfolioCollectionId("all");
}, [selectedPortfolioClientId]);

const portfolioFocusResolved = useMemo(() => {
  if (!portfolioBaseFocus) return null;
  if (selectedPortfolioCollectionId === "all") return portfolioBaseFocus;

  const allArtworks = (portfolioSummary?.rawArtworks || []).filter(
    (row) => row.client_id === portfolioBaseFocus.clientId
  );

  const filteredRows = allArtworks.filter((row) => {
    return row?.collection_id === selectedPortfolioCollectionId;
  });

  const filteredSummary = computePortfolioSummary(
    filteredRows,
    { [portfolioBaseFocus.clientId]: { displayName: portfolioBaseFocus.clientName } },
    portfolioSummary?.collectionsById || {}
  );

  return filteredSummary?.dominantClient || filteredSummary || null;
}, [portfolioBaseFocus, selectedPortfolioCollectionId, portfolioSummary, computePortfolioSummary]);

  const ServiceKanbanCard = ({ request }) => {
    const st = request.status ?? "pending";
    const ownedByMe = request.expert_id && user?.id && request.expert_id === user.id;
    const client = request.profiles;

    const clientName = client
      ? `${client.first_name || ""} ${client.last_name || ""}`.trim()
      : request?.client_id
        ? dashboardT("complete.services.collectorWithId", { id: String(request.client_id).slice(0, 8) })
        : dashboardT("complete.services.collector");

    const typeLabel = (() => {
      const tt = (request?.service_type || "").toLowerCase();
      const map = {
        custom: dashboardT("complete.services.types.custom"),
        validation: dashboardT("complete.services.types.validation"),
        authentication: dashboardT("complete.services.types.authentication"),
        placement_institution: dashboardT("complete.services.types.placementInstitution"),
        placement_auction: dashboardT("complete.services.types.placementAuction"),
      };
      return map[tt] || (request.service_type || dashboardT("complete.services.types.service"));
    })();

    const isInvitation = ["expert_invitation", "artwork_share_invitation"].includes(request?.metadata?.source);

    const brief = (request?.description || request?.notes || "").trim();

    const conversationId = conversationsByServiceRequest[request.id];
    const unreadCount = conversationId ? (unreadByConversation?.[conversationId] || 0) : 0;

    const statusColors = {
      pending: { border: "#1e3a8a", bg: "#eff6ff", badge: "#1e3a8a", shadow: "rgba(30, 58, 138, 0.15)" },
      in_progress: { border: "#92400e", bg: "#fef9e7", badge: "#92400e", shadow: "rgba(146, 64, 14, 0.15)" },
      awaiting_feedback: { border: "#c2410c", bg: "#fff7ed", badge: "#c2410c", shadow: "rgba(194, 65, 12, 0.15)" },
      completed: { border: "#065f46", bg: "#ecfdf5", badge: "#065f46", shadow: "rgba(6, 95, 70, 0.15)" },
      cancelled: { border: "#991b1b", bg: "#fef2f2", badge: "#991b1b", shadow: "rgba(153, 27, 27, 0.15)" },
    };

    const colors = statusColors[st] || statusColors.pending;

    const artworkId = request?.metadata?.artwork_id || null;

    return (
      <div
        style={{
          background: "#fff",
          border: isInvitation ? `3px solid ${colors.border}` : "1px solid #e5e3df",
          borderRadius: 8,
          padding: 16,
          marginBottom: 12,
          boxShadow: isInvitation ? `0 4px 6px ${colors.shadow}` : "0 1px 3px rgba(0,0,0,0.04)",
          position: "relative",
        }}
      >
        {isInvitation && (
          <div
            style={{
              position: "absolute",
              top: 8,
              right: 8,
              background: colors.badge,
              color: "#fff",
              padding: "4px 10px",
              borderRadius: 12,
              fontSize: 10,
              fontWeight: 700,
              fontFamily: "system-ui, sans-serif",
              letterSpacing: "0.5px",
              textTransform: "uppercase",
              zIndex: 2,
            }}
          >
            {dashboardT("complete.services.invitation")}
          </div>
        )}

        {unreadCount > 0 && (
          <div
            style={{
              position: "absolute",
              top: isInvitation ? 40 : 8,
              right: 8,
              background: colors.badge,
              color: "#fff",
              borderRadius: "50%",
              fontSize: 10,
              fontWeight: 700,
              fontFamily: "system-ui, sans-serif",
              minWidth: 20,
              height: 20,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 1,
            }}
            title={dashboardT("complete.services.newMessages")}
          >
            {unreadCount}
          </div>
        )}

        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 12 }}>
          <div style={{ flex: 1, paddingRight: isInvitation ? 90 : 0 }}>
            <div style={{ fontSize: 14, fontWeight: 500, color: "#1a1a1a", fontFamily: "Georgia, serif", marginBottom: 6 }}>
              {typeLabel}
            </div>
            <div style={{ fontSize: 12, color: "#6b6b6b", fontFamily: "system-ui, sans-serif" }}>
              {clientName}
            </div>
          </div>
          {!isInvitation && request.urgent && <AlertTriangle size={16} color="#ef4444" />}
        </div>

        {brief && (
          <div
            style={{
              fontSize: 12,
              color: "#4a4a4a",
              fontFamily: "system-ui, sans-serif",
              lineHeight: 1.5,
              marginBottom: 12,
              paddingTop: 12,
              borderTop: "1px solid #f5f5f4",
            }}
          >
            {brief.length > 140 ? `${brief.substring(0, 140)}...` : brief}
          </div>
        )}

        <div style={{ display: "flex", gap: 8, fontSize: 11, color: "#9ca3af", fontFamily: "system-ui, sans-serif", marginBottom: 12 }}>
          <Clock size={12} />
          {new Date(request.created_at).toLocaleDateString(locale)}
        </div>


 {artworkId && (
   <button
     type="button"
     onClick={() => navigate(`/artwork/${artworkId}`)}
     style={{
       width: "100%",
       padding: "10px 14px",
       background: "#111",
       color: "#fff",
       border: "none",
       borderRadius: 6,
       fontFamily: "system-ui, sans-serif",
       fontSize: 12,
       cursor: "pointer",
       fontWeight: 600,
       marginBottom: 8,
     }}
   >
     👁 {dashboardT("complete.services.openArtwork")}
   </button>
 )}

        {ownedByMe && st === "pending" && (
          <button
            type="button"
            onClick={() => startRequest(request.id)}
            style={{
              width: "100%",
              padding: "10px 14px",
              background: "#3b82f6",
              color: "#fff",
              border: "none",
              borderRadius: 6,
              fontFamily: "system-ui, sans-serif",
              fontSize: 12,
              cursor: "pointer",
              fontWeight: 600,
              transition: "all 0.2s ease",
            }}
            onMouseEnter={(e) => (e.currentTarget.style.background = "#2563eb")}
            onMouseLeave={(e) => (e.currentTarget.style.background = "#3b82f6")}
          >
            ▶ {dashboardT("complete.services.start")}
          </button>
        )}

        {ownedByMe && st === "in_progress" && (
          <button
            type="button"
            onClick={() => markReflection(request.id)}
            style={{
              width: "100%",
              padding: "10px 14px",
              background: "#10b981",
              color: "#fff",
              border: "none",
              borderRadius: 6,
              fontFamily: "system-ui, sans-serif",
              fontSize: 12,
              cursor: "pointer",
              fontWeight: 600,
              transition: "all 0.2s ease",
            }}
            onMouseEnter={(e) => (e.currentTarget.style.background = "#059669")}
            onMouseLeave={(e) => (e.currentTarget.style.background = "#10b981")}
          >
            ✓ {dashboardT("complete.services.markReflection")}
          </button>
        )}

        {ownedByMe && (st === "in_progress" || st === "awaiting_feedback") && (
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {st === "awaiting_feedback" && (
              <>
                <div
                  style={{
                    padding: "8px 12px",
                    background: "#fff7ed",
                    border: "1px solid #fed7aa",
                    borderRadius: 6,
                    fontSize: 11,
                    color: "#c2410c",
                    fontFamily: "system-ui, sans-serif",
                    textAlign: "center",
                    fontWeight: 500,
                  }}
                >
                  ⏳ {dashboardT("complete.services.awaitingClientFeedback")}
                </div>

                <div style={{ display: "flex", gap: 8 }}>
                  <button
                    type="button"
                    onClick={() => {
                      setRequestToAccept(request);
                      setShowAcceptModal(true);
                    }}
                    style={{
                      flex: 1,
                      padding: "10px 14px",
                      background: "#10b981",
                      color: "#fff",
                      border: "none",
                      borderRadius: 6,
                      fontFamily: "system-ui, sans-serif",
                      fontSize: 12,
                      cursor: "pointer",
                      fontWeight: 600,
                      transition: "all 0.2s ease",
                    }}
                    onMouseEnter={(e) => (e.currentTarget.style.background = "#059669")}
                    onMouseLeave={(e) => (e.currentTarget.style.background = "#10b981")}
                  >
                    ✓ {dashboardT("complete.services.accept")}
                  </button>

                  <button
                    type="button"
                    onClick={() => rejectClient(request.id)}
                    style={{
                      flex: 1,
                      padding: "10px 14px",
                      background: "#fff",
                      color: "#ef4444",
                      border: "2px solid #fecaca",
                      borderRadius: 6,
                      fontFamily: "system-ui, sans-serif",
                      fontSize: 12,
                      cursor: "pointer",
                      fontWeight: 600,
                      transition: "all 0.2s ease",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = "#fef2f2";
                      e.currentTarget.style.borderColor = "#ef4444";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = "#fff";
                      e.currentTarget.style.borderColor = "#fecaca";
                    }}
                  >
                    ✕ {dashboardT("complete.services.reject")}
                  </button>
                </div>
              </>
            )}

            <button
              type="button"
              onClick={() => {
                const subject = `${typeLabel} • ${clientName}`.trim();
                openConversationForRequest(request, subject);
              }}
              style={{
                width: "100%",
                padding: "10px 14px",
                background: "#4b5563",
                color: "#fff",
                border: "none",
                borderRadius: 6,
                fontFamily: "system-ui, sans-serif",
                fontSize: 12,
                cursor: "pointer",
                fontWeight: 600,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 6,
                transition: "all 0.2s ease",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = "#374151";
                e.currentTarget.style.transform = "translateY(-1px)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = "#4b5563";
                e.currentTarget.style.transform = "translateY(0)";
              }}
            >
              <Mail size={14} />
              {dashboardT("complete.services.chatWithClient")}
            </button>
          </div>
        )}

        {ownedByMe && st === "completed" && (
          <div style={{ marginTop: 12, paddingTop: 12, borderTop: "1px solid #f5f5f4" }}>
            {isInvitation ? (
              <ExpertClientAccessStatus expertId={user.id} clientId={request.client_id} />
            ) : (
              <div
                style={{
                  padding: "8px 12px",
                  background: "#ecfdf5",
                  border: "1px solid #10b981",
                  borderRadius: 6,
                  fontSize: 11,
                  color: "#065f46",
                  fontFamily: "system-ui, sans-serif",
                  textAlign: "center",
                  fontWeight: 500,
                }}
              >
                ✓ {dashboardT("complete.services.completedOn", {
                  date: request.completed_at
                    ? new Date(request.completed_at).toLocaleDateString(locale)
                    : new Date(request.created_at).toLocaleDateString(locale),
                })}
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  // Guard: évite le crash page blanche lors de la navigation SPA
  if (!user?.id) {
    return (
      <div style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "#fafaf8",
        fontFamily: "Georgia, serif",
      }}>
        <div style={{
          width: 40, height: 40,
          border: "3px solid #e5e3df",
          borderTopColor: "#1a1a1a",
          borderRadius: "50%",
          animation: "spin 1s linear infinite",
          margin: "0 auto",
        }} />
      </div>
    );
  }

  const planLabel = profileBasics?.plan === "art_advisor_premium" ? "Art Advisor Premium"
    : profileBasics?.plan === "art_advisor" ? "Art Advisor"
    : profileBasics?.plan === "family_office" ? "Family Office"
    : dashboardT("complete.common.expert");

  const expertPhotoUrl = expertRow?.image_url || DEFAULT_EXPERT_IMAGE;
  const expertName = expertRow?.name || (profileBasics ? `${profileBasics.first_name || ""} ${profileBasics.last_name || ""}`.trim() : "") || dashboardT("complete.common.expert");
  const expertSpecialty = expertRow?.specialty || planLabel;

const sideNavItems = [
  { key: "overview",       label: dashboardT("actions.overview"), icon: Activity },
  { key: "clients",        label: dashboardT("actions.clients"),          icon: Users },
  { key: "collections",    label: dashboardT("actions.collections"),      icon: Package },
  { key: "missions",       label: dashboardT("actions.missions"),         icon: Clipboard },
  { key: "propositions",   label: dashboardT("actions.propositions", "Propositions"), icon: Package, route: "/expert/propositions" },
  { key: "services",       label: dashboardT("actions.services"),         icon: Clipboard },
  { key: "outils",         label: dashboardT("actions.outils"),           icon: Warehouse, badge: dashboardT("actions.new") },
  { key: "collaborateurs", label: dashboardT("actions.collaborateurs"),   icon: UserPlus },
  { key: "billing",        label: dashboardT("actions.billing"),      icon: CreditCard },
  { key: "profile",        label: dashboardT("actions.profile"),       icon: UserCircle },
];

  return (
    <div className="expert-dashboard-layout" style={{ display: "flex", minHeight: "100vh", background: "#fafaf8", fontFamily: "Georgia, serif" }}>
      {/* ═══ SIDEBAR GAUCHE ═══ */}
      <div className="expert-dashboard-sidebar" style={{
        width: 200, flexShrink: 0,
        background: "#fff",
        borderRight: "1px solid #e5e3df",
        display: "flex", flexDirection: "column",
        position: "sticky", top: 0, height: "100vh", overflowY: "auto",
      }}>
        {/* Expert identity */}
        <div style={{ padding: "24px 16px 20px", borderBottom: "1px solid #f2efe9" }}>
          <div style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: "0.08em", color: "#9ca3af", fontFamily: "system-ui, sans-serif", marginBottom: 12 }}>{dashboardT("complete.common.expert")}</div>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
            <img
              src={expertPhotoUrl}
              alt={expertName}
              style={{ width: 40, height: 40, borderRadius: "50%", objectFit: "cover", border: "2px solid #f2efe9", flexShrink: 0 }}
              onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = DEFAULT_EXPERT_IMAGE; }}
            />
            <div>
              <div style={{ fontSize: 13, fontWeight: 600, color: "#1a1a1a", fontFamily: "system-ui, sans-serif", lineHeight: 1.3 }}>{expertName}</div>
              <div style={{ fontSize: 11, color: "#c9a961", fontFamily: "system-ui, sans-serif", fontWeight: 500 }}>{expertSpecialty}</div>
            </div>
          </div>
          {profileBasics?.plan && (
            <div style={{ display: "inline-flex", alignItems: "center", padding: "3px 8px", background: "#fff7ed", border: "1px solid #fed7aa", borderRadius: 999, fontSize: 10, color: "#c2410c", fontWeight: 700, fontFamily: "system-ui, sans-serif", letterSpacing: "0.04em" }}>
              {planLabel}
            </div>
          )}
        </div>

        {/* Navigation */}
        <nav style={{ flex: 1, padding: "12px 8px" }}>
	    {sideNavItems.map(({ key, label, icon: Icon, badge, route }) => (
            <button
              key={key}
              type="button"
              onClick={() => (route ? navigate(route) : setTab(key))}
              style={{
                width: "100%", display: "flex", alignItems: "center", gap: 10,
                padding: "10px 12px", marginBottom: 2,
                background: tab === key ? "#f9f7f4" : "transparent",
                border: "none", borderRadius: 6,
                color: tab === key ? "#1a1a1a" : "#6b6b6b",
                fontFamily: "system-ui, sans-serif", fontSize: 13,
                fontWeight: tab === key ? 600 : 400,
                cursor: "pointer", textAlign: "left",
                borderLeft: tab === key ? "3px solid #1a1a1a" : "3px solid transparent",
                transition: "all 0.15s",
              }}
            >
              <Icon size={16} strokeWidth={tab === key ? 2 : 1.5} />
              <span style={{ flex: 1 }}>{label}</span>
              {badge && (
                <span style={{ fontSize: 9, background: "#c9a961", color: "#fff", padding: "2px 5px", borderRadius: 999, fontWeight: 700, letterSpacing: "0.04em" }}>
                  {badge}
                </span>
              )}
              {key === "services" && totalUnread > 0 && (
                <span style={{ fontSize: 10, background: "#ef4444", color: "#fff", borderRadius: "50%", minWidth: 18, height: 18, display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700, fontFamily: "system-ui, sans-serif" }}>
                  {totalUnread}
                </span>
              )}
            </button>
          ))}
        </nav>

        {/* Bloc plan en bas */}
        <div style={{ padding: "16px", margin: "0 8px 16px", background: "#fafaf8", borderRadius: 6, border: "1px solid #e5e3df" }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: "#1a1a1a", fontFamily: "system-ui, sans-serif", marginBottom: 8 }}>
            {dashboardT("complete.sidebar.plan", { plan: planLabel })}
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 12 }}>
            {[
              dashboardT("complete.sidebar.features.unlimitedClients"),
              dashboardT("complete.sidebar.features.fullAi"),
              dashboardT("complete.sidebar.features.customReports"),
            ].map((f) => (
              <div key={f} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11, color: "#4b5563", fontFamily: "system-ui, sans-serif" }}>
                <svg width="12" height="12" viewBox="0 0 12 12"><polyline points="1.5,6 4.5,9 10.5,3" stroke="#22c55e" strokeWidth="1.8" fill="none" strokeLinecap="round" strokeLinejoin="round"/></svg>
                {f}
              </div>
            ))}
          </div>
          <button
            type="button"
            onClick={() => navigate("/expert/profile#subscription")}
            style={{ width: "100%", padding: "8px 0", background: "transparent", border: "none", color: "#c9a961", fontFamily: "system-ui, sans-serif", fontSize: 12, fontWeight: 600, cursor: "pointer", display: "flex", alignItems: "center", gap: 4 }}
          >
            {dashboardT("complete.sidebar.manageSubscription")} →
          </button>
        </div>
      </div>

      {/* ═══ CONTENU PRINCIPAL ═══ */}
      <div className="expert-dashboard-main" style={{ flex: 1, padding: "32px 32px 48px", overflowX: "hidden", minWidth: 0 }}>
        <div style={{ maxWidth: 1200, margin: "0 auto" }}>
  
{tab === "overview" && (
  <>
    {/* ═══ Cockpit quotidien (P0) : priorités, KPIs métier réels, clients à suivre ═══ */}
    <AdvisorCockpit
      summary={dashboardSummary}
      loading={summaryLoading}
      firstName={profileBasics?.first_name || ""}
      lang={i18n.resolvedLanguage || i18n.language || "fr"}
      opportunities={opportunities}
      artworksRequiringAction={artworksRequiringAction}
      onCreateMission={() => navigate("/expert/dashboard?tab=missions&new=1")}
      onInviteClient={() => setTab("clients")}
      onOpenCollection={() => setTab("collections")}
      onProduceReport={() => setTab("outils")}
      onImportFiles={() => navigate("/import-batch")}
      onKpiClick={(key) => {
        // Œuvres à traiter : la file readiness est déjà affichée juste en dessous.
        if (key === "artworks_requiring_action") return;
        const focusByKpi = {
          missions_in_progress: "active",
          deliverables_ready: "ready_to_deliver",
          ready_to_invoice: "to_invoice",
          clients_to_follow_up: "follow_up",
        };
        const f = focusByKpi[key];
        navigate(`/expert/dashboard?tab=missions${f ? `&focus=${f}` : ""}`);
      }}
      onPriorityClick={(p) => {
        const focusByKind = {
          overdue: "follow_up",
          deliverable: "ready_to_deliver",
          to_invoice: "to_invoice",
          follow_up: "follow_up",
        };
        const f = focusByKind[p?.kind];
        navigate(`/expert/dashboard?tab=missions${f ? `&focus=${f}` : ""}`);
      }}
      onClientClick={(clientId) => {
        if (clientId) setSelectedPortfolioClientId(clientId);
        setAnalysisOpen(true);
      }}
      onProposeMission={(op) => convertOpportunity(op, false)}
      onDraftMission={(op) => convertOpportunity(op, true)}
      onDismissOpportunity={(op) => recordOpportunityAction(op, "dismissed")}
      onSnoozeOpportunity={(op) =>
        recordOpportunityAction(op, "snoozed", {
          snoozed_until: new Date(Date.now() + 30 * 24 * 3600 * 1000).toISOString(),
        })
      }
    />
    <ExpertCircleReferralCard />

    {/* ═══ File de travail readiness (§6) : rubriques → œuvres → mission groupée ═══ */}
    <AdvisorReadinessQueue
      artworks={secureArtworks}
      collectionNames={collectionNames}
      clientNames={clientNamesMap}
      lang={i18n.resolvedLanguage || i18n.language || "fr"}
      busy={missionBusy}
      onCreateMission={createGroupedMission}
    />

    {/* ═══ Analyse du portefeuille client — vue de second niveau, repliée par défaut (§9) ═══ */}
    <button
      type="button"
      onClick={() => setAnalysisOpen((o) => !o)}
      style={{
        width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between",
        gap: 12, padding: "14px 18px", marginBottom: analysisOpen ? 20 : 12,
        background: "#fff", border: "1px solid #e5e3df", borderRadius: 8,
        cursor: "pointer", textAlign: "left",
      }}
    >
      <span style={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <span style={{ fontFamily: "Georgia, serif", fontSize: 17, color: "#1a1a1a" }}>
          {dashboardT("overview.portfolioAnalysisTitle", { defaultValue: "Analyse du portefeuille client" })}
        </span>
        <span style={{ fontFamily: "system-ui, sans-serif", fontSize: 12.5, color: "#6b6b6b" }}>
          {dashboardT("overview.portfolioAnalysisHint", { defaultValue: "Valeur, artistes, documentation et conservation — en détail." })}
        </span>
      </span>
      <ChevronDown size={20} strokeWidth={1.6} color="#8b7355" style={{ flexShrink: 0, transform: analysisOpen ? "rotate(180deg)" : "none", transition: "transform 0.2s" }} />
    </button>

    {analysisOpen && (
      <>
    <div
      style={{
        background: "linear-gradient(135deg, #ffffff 0%, #fafaf8 100%)",
        border: "1px solid #e5e3df",
        borderRadius: 8,
        padding: 28,
        marginBottom: 24,
        boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", gap: 16, alignItems: "flex-start", flexWrap: "wrap", marginBottom: 20 }}>
        <div>
          <div style={{ fontSize: 12, color: "#8b7355", fontFamily: "system-ui, sans-serif", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 8 }}>
            {dashboardT("portfolio.titleLabel")}
          </div>
          <h2 style={{ fontFamily: "Georgia, serif", fontSize: 26, fontWeight: 400, color: "#1a1a1a", margin: 0, letterSpacing: "-0.02em" }}>
            {dashboardT("portfolio.title")}
          </h2>
          <p style={{ marginTop: 10, marginBottom: 0, maxWidth: 760, fontSize: 14, color: "#6b6b6b", lineHeight: 1.7, fontFamily: "system-ui, sans-serif" }}>
           {dashboardT("portfolio.subtitle")}
          </p>
        </div>

<div style={{ display: "flex", gap: 10, alignItems: "flex-end", flexWrap: "wrap" }}>
  <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
    <div style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: "0.06em", color: "#9ca3af", fontFamily: "system-ui, sans-serif" }}>
      {dashboardT("portfolio.focus")}
    </div>

    <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
      <div style={{ position: "relative" }}>
        <select
          value={selectedPortfolioClientId}
          onChange={(e) => setSelectedPortfolioClientId(e.target.value)}
          style={{
            minWidth: 250,
            padding: "10px 36px 10px 14px",
            border: "1px solid #d6d3d1",
            borderRadius: 6,
            background: "#fff",
            color: "#1a1a1a",
            fontFamily: "system-ui, sans-serif",
            fontSize: 14,
            appearance: "none",
            cursor: "pointer",
          }}
        >
          <option value="auto">
            {dashboardT("portfolio.autoMajorClient")}
          </option>

          {portfolioClientOptions.length === 0 && (
            <option disabled>
              {dashboardT("portfolio.noSharedCollection")}
            </option>
          )}

          {portfolioClientOptions.map((client) => (
            <option key={client.clientId} value={client.clientId}>
              {client.clientName}
            </option>
          ))}
        </select>

        <svg
          style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", pointerEvents: "none" }}
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="#6b6b6b"
          strokeWidth="2"
        >
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </div>

      {portfolioClientOptions.length === 0 && !loadingPortfolio ? (
        <div style={{ padding: "8px 12px", border: "1px solid #fecaca", borderRadius: 6, background: "#fef2f2", fontSize: 12, color: "#991b1b", fontFamily: "system-ui, sans-serif" }}>
          {dashboardT("portfolio.noClientCollectionTitle")}
        </div>
      ) : (
        <div style={{ padding: "10px 14px", border: "1px solid #e5e3df", borderRadius: 999, background: "#fff", fontSize: 12, color: "#6b6b6b", fontFamily: "system-ui, sans-serif", whiteSpace: "nowrap" }}>
          {loadingPortfolio
            ? dashboardT("portfolio.updating")
            : dashboardT("portfolio.artworksAnalyzedTotal", {
                count: portfolioFocusResolved?.totalArtworks || 0,
              })}
        </div>
      )}
    </div>
  </div>
</div>
          {portfolioCollectionOptions.length > 0 && (
            <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
              <div style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: "0.06em", color: "#9ca3af", fontFamily: "system-ui, sans-serif" }}>{dashboardT("portfolio.collection")}</div>
              <div style={{ position: "relative" }}>
                <select
                  value={selectedPortfolioCollectionId}
                  onChange={(e) => setSelectedPortfolioCollectionId(e.target.value)}
                  style={{ minWidth: 210, padding: "10px 36px 10px 14px", border: "1px solid #d6d3d1", borderRadius: 6, background: "#fff", color: "#1a1a1a", fontFamily: "system-ui, sans-serif", fontSize: 14, appearance: "none", cursor: "pointer" }}
                >
                  <option value="all">{dashboardT("portfolio.allCollections")}</option>
                  {portfolioCollectionOptions.map((col) => (
                    <option key={col.collectionId} value={col.collectionId}>{col.collectionName || dashboardT("complete.portfolio.untitled")}</option>
                  ))}
                </select>
                <svg style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", pointerEvents: "none" }} width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#6b6b6b" strokeWidth="2"><polyline points="6 9 12 15 18 9" /></svg>
              </div>
            </div>
          )}
        </div>
      </div>

      {!portfolioFocusResolved && !loadingPortfolio && (
        <div style={{ padding: "40px 24px", textAlign: "center", background: "#fafaf8", border: "1px dashed #d6d3d1", borderRadius: 8, margin: "8px 0" }}>
          <div style={{ fontSize: 15, color: "#6b6b6b", fontFamily: "system-ui, sans-serif", marginBottom: 8 }}>{dashboardT("complete.portfolio.noAccessibleCollection")}</div>
          <div style={{ fontSize: 13, color: "#9ca3af", fontFamily: "system-ui, sans-serif", lineHeight: 1.6, marginBottom: 20 }}>
            {dashboardT("portfolio.noClientCollectionText")}
            {dashboardT("complete.portfolio.inviteFromClientsTab")}
          </div>
          <button type="button" onClick={() => setTab("clients")} style={{ padding: "10px 24px", background: "#1a1a1a", color: "#fff", border: "none", borderRadius: 6, fontFamily: "system-ui, sans-serif", fontSize: 13, cursor: "pointer", fontWeight: 500 }}>
            {dashboardT("portfolio.inviteClient")}
          </button>
        </div>
      )}

      {portfolioFocusResolved && (
        <>
          <div
            style={{
              background: portfolioFocusResolved.usesAI ? "#fff7ed" : "#f8fafc",
              border: `1px solid ${portfolioFocusResolved.usesAI ? "#fed7aa" : "#dbe4ee"}`,
              borderRadius: 6,
              padding: 16,
              marginBottom: 18,
            }}
          >
            <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
              <ShieldCheck size={18} color={portfolioFocusResolved.usesAI ? "#b45309" : "#475569"} />
              <div>
                <div style={{ fontSize: 13, fontWeight: 700, color: "#1a1a1a", fontFamily: "system-ui, sans-serif", marginBottom: 4 }}>
                  {portfolioFocusResolved.usesAI
                    ? dashboardT("complete.portfolio.analysisTitles.ai")
                    : dashboardT("complete.portfolio.analysisTitles.rules")}
                </div>
                <div style={{ fontSize: 12, color: "#6b6b6b", fontFamily: "system-ui, sans-serif", lineHeight: 1.6 }}>
{portfolioFocusResolved.usesAI
  ? dashboardT("portfolio.aiEnhancedText")
  : dashboardT("portfolio.ruleBasedText")}
                </div>
              </div>
            </div>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1.2fr 1.1fr 0.9fr", gap: 16, marginBottom: 20 }}>
            <div style={{ background: "#fff", border: "1px solid #ece9e4", borderRadius: 6, padding: 20 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 12 }}>
                <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.06em", color: "#9ca3af", fontFamily: "system-ui, sans-serif" }}>
                  {isPortfolioFocusedOnMajor
  ? dashboardT("portfolio.majorClient")
  : dashboardT("portfolio.selectedClient")}
                </div>
                {selectedPortfolioCollectionId !== "all" && (
                  <div style={{ fontSize: 11, color: "#8b7355", background: "#fffaf0", border: "1px solid #f3e7c9", borderRadius: 999, padding: "3px 8px", fontFamily: "system-ui, sans-serif" }}>
                    {portfolioBaseFocus?.collectionSummaries?.find(c => c.collectionId === selectedPortfolioCollectionId)?.collectionName || dashboardT("complete.portfolio.collectionFallback")}
                  </div>
                )}
              </div>
              <div style={{ fontSize: 26, fontWeight: 400, color: "#1a1a1a", fontFamily: "Georgia, serif", marginBottom: 12, lineHeight: 1.2 }}>
                {portfolioFocusResolved?.clientName || "—"}
              </div>
              <div style={{ fontSize: 13, color: "#6b6b6b", lineHeight: 1.7, fontFamily: "system-ui, sans-serif", marginBottom: 14 }}>
                {portfolioFocusResolved?.isPortfolioReady
                  ? dashboardT("complete.portfolio.readyDescription")
                  : dashboardT("complete.portfolio.structuringDescription")}
              </div>
              {!portfolioFocusResolved?.isPortfolioReady && (portfolioFocusResolved?.readinessMissing || []).length > 0 && (
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                  {portfolioFocusResolved.readinessMissing.slice(0, 2).map((item) => (
                    <span key={item} style={{ fontSize: 11, color: "#6b4f1d", background: "#fffaf0", border: "1px solid #f3e7c9", borderRadius: 999, padding: "5px 10px", fontFamily: "system-ui, sans-serif" }}>{item}</span>
                  ))}
                </div>
              )}
            </div>

            <div style={{ background: "#fff", border: "1px solid #ece9e4", borderRadius: 6, padding: 20 }}>
              <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.06em", color: "#9ca3af", fontFamily: "system-ui, sans-serif", marginBottom: 14 }}>{dashboardT("portfolio.portfolioReadyRules")}</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                {[
  {
    label: dashboardT("portfolio.ruleValue"),
    value: euro(portfolioFocusResolved?.totalValue || 0),
    ok: (portfolioFocusResolved?.totalValue || 0) >= 150000,
  },
  {
    label: dashboardT("portfolio.ruleArtworks"),
    value: String(portfolioFocusResolved?.totalArtworks || 0),
    ok: (portfolioFocusResolved?.totalArtworks || 0) >= 12,
  },
  {
    label: dashboardT("portfolio.ruleArtists"),
    value: String(portfolioFocusResolved?.distinctArtists || 0),
    ok: (portfolioFocusResolved?.distinctArtists || 0) >= 3,
  },
  {
    label: dashboardT("portfolio.ruleCategories"),
    value: String(portfolioFocusResolved?.distinctCategories || 0),
    ok: (portfolioFocusResolved?.distinctCategories || 0) >= 2,
  },
  {
    label: dashboardT("portfolio.ruleValuationCoverage"),
    value: `${portfolioFocusResolved?.valuationCoverage || 0}%`,
    ok: (portfolioFocusResolved?.valuationCoverage || 0) >= 60,
  },
].map((rule) => (
                  <div key={rule.label} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, flex: 1 }}>
                      <div style={{ width: 18, height: 18, borderRadius: "50%", border: `2px solid ${rule.ok ? "#22c55e" : "#d6d3d1"}`, background: rule.ok ? "#22c55e" : "transparent", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                        {rule.ok && <svg width="10" height="10" viewBox="0 0 10 10" fill="none"><polyline points="1.5,5 4,7.5 8.5,2.5" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></svg>}
                      </div>
                      <span style={{ fontSize: 12, color: "#4b5563", fontFamily: "system-ui, sans-serif" }}>{rule.label}</span>
                    </div>
                    <span style={{ fontSize: 12, fontWeight: 600, color: rule.ok ? "#16a34a" : "#6b7280", fontFamily: "system-ui, sans-serif", whiteSpace: "nowrap" }}>{rule.value} {rule.ok ? "✓" : "○"}</span>
                  </div>
                ))}
              </div>
            </div>

            <div style={{ background: "#fff", border: "1px solid #ece9e4", borderRadius: 6, padding: 20 }}>
              <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.06em", color: "#9ca3af", fontFamily: "system-ui, sans-serif", marginBottom: 14 }}>{dashboardT("portfolio.patrimonialLevel")}</div>
              {portfolioFocusResolved?.maturityLabel && (
                <div style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: "8px 14px", borderRadius: 999, background: portfolioFocusResolved.maturityLevel >= 3 ? "#ecfdf5" : "#fff7ed", border: `1px solid ${portfolioFocusResolved.maturityLevel >= 3 ? "#a7f3d0" : "#fed7aa"}`, marginBottom: 14 }}>
                  <div style={{ width: 10, height: 10, borderRadius: "50%", background: portfolioFocusResolved.maturityLevel >= 4 ? "#16a34a" : "#f59e0b" }} />
                  <span style={{ fontSize: 13, fontWeight: 600, color: portfolioFocusResolved.maturityLevel >= 3 ? "#065f46" : "#9a3412", fontFamily: "system-ui, sans-serif" }}>{portfolioFocusResolved.maturityLabel}</span>
                </div>
              )}
              <div style={{ fontSize: 12, color: "#6b6b6b", fontFamily: "system-ui, sans-serif", lineHeight: 1.7 }}>
                {portfolioFocusResolved?.isPatrimonialReady
  ? dashboardT("portfolio.strongPatrimonyReached")
  : portfolioFocusResolved?.isPortfolioReady
    ? dashboardT("portfolio.closeToStrongPatrimony")
    : dashboardT("portfolio.collectionStructuring")}
              </div>
              {!portfolioFocusResolved?.isPatrimonialReady && (
                <div style={{ marginTop: 10, fontSize: 12, color: "#9ca3af", fontFamily: "system-ui, sans-serif", lineHeight: 1.6 }}>
                  {dashboardT("portfolio.recommendedObjective")} :<br />{dashboardT("portfolio.objectiveValue")}≥ <br />{dashboardT("portfolio.objectiveArtworks")}
                </div>
              )}
            </div>
          </div>

<div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(175px, 100%), 1fr))", gap: 14, marginBottom: 20 }}>
  {[
    {
      label: dashboardT("complete.portfolio.metrics.trackedValue"),
      value: euro(portfolioFocusResolved?.totalValue || 0),
      note: `${dashboardT("complete.portfolio.metrics.valuationCoverageNote", { coverage: portfolioFocusResolved?.valuationCoverage || 0 })} ${valuationText(String(locale || "fr").slice(0, 2)).caption}`,
    },
    {
      label: dashboardT("complete.portfolio.metrics.artworks"),
      value: String(portfolioFocusResolved?.totalArtworks || 0),
      note: dashboardT("complete.portfolio.metrics.artistsCategoriesNote", {
        artists: portfolioFocusResolved?.distinctArtists || 0,
        categories: portfolioFocusResolved?.distinctCategories || 0,
      }),
    },
    {
      label: dashboardT("complete.portfolio.metrics.artistConcentration"),
      value: `${portfolioFocusResolved?.artistConcentration || 0}%`,
      note: (portfolioFocusResolved?.topArtists || []).length > 0
        ? dashboardT("complete.portfolio.metrics.topThreeShare")
        : dashboardT("complete.portfolio.metrics.dominantArtistsShare"),
    },
    {
      label: dashboardT("complete.portfolio.metrics.technicalDiversity"),
      value: dashboardT("complete.portfolio.metrics.techniquesCount", { count: portfolioFocusResolved?.distinctTechniques || 0 }),
      note: portfolioFocusResolved?.technicalDiversityLabel || dashboardT("complete.portfolio.metrics.techniquesRecorded"),
    },
    {
      label: dashboardT("complete.portfolio.metrics.documentationCoverage"),
      value: `${portfolioFocusResolved?.documentationCoverage || 0}%`,
      note: dashboardT("complete.portfolio.metrics.documentationNote"),
    },
    {
      label: dashboardT("portfolio.liquidityPotential"),
      value: portfolioFocusResolved?.liquidityLevel || "Limitée",
      note: `${portfolioFocusResolved?.liquidityAverage || 0}/100 · ${dashboardT("portfolio.liquidityNote")}`,
    },
  ].map((item) => (
    <div key={item.label} style={{ background: "#fff", border: "1px solid #ece9e4", borderRadius: 6, padding: 18 }}>
      <div style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: "0.06em", color: "#9ca3af", fontFamily: "system-ui, sans-serif", marginBottom: 8 }}>{item.label}</div>
      <div style={{ fontSize: 26, fontWeight: 300, color: "#1a1a1a", fontFamily: "Georgia, serif", marginBottom: 6, lineHeight: 1.1 }}>{item.value}</div>
      <div style={{ fontSize: 11, color: "#6b6b6b", lineHeight: 1.5, fontFamily: "system-ui, sans-serif" }}>{item.note}</div>
    </div>
  ))}
</div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(280px, 100%), 1fr))", gap: 16 }}>
            <div style={{ background: "#fff", border: "1px solid #ece9e4", borderRadius: 6, padding: 18 }}>
<div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, marginBottom: 14 }}>
  <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.06em", color: "#9ca3af", fontFamily: "system-ui, sans-serif" }}>
    {topArtistsMode === "count"
      ? dashboardT("portfolio.dominantArtistsByCount")
      : dashboardT("portfolio.dominantArtists")}
  </div>

  <button
    type="button"
    onClick={() => setTopArtistsMode((mode) => (mode === "value" ? "count" : "value"))}
    style={{
      border: "1px solid #e5e3df",
      background: "#fff",
      padding: "5px 9px",
      borderRadius: 999,
      fontSize: 11,
      color: "#8b7355",
      fontFamily: "system-ui, sans-serif",
      cursor: "pointer",
      whiteSpace: "nowrap",
    }}
  >
    {topArtistsMode === "value"
      ? dashboardT("portfolio.showByArtworkCount")
      : dashboardT("portfolio.showByValue")}
  </button>
</div>

<div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
  {((topArtistsMode === "count" ? portfolioFocusResolved?.topArtistsByCount : portfolioFocusResolved?.topArtists) || []).length > 0 ? (
    ((topArtistsMode === "count" ? portfolioFocusResolved?.topArtistsByCount : portfolioFocusResolved?.topArtists) || [])
      .slice(0, showAllTopArtists ? 10 : 5)
      .map((artist, i) => (
        <div key={`${topArtistsMode}-${artist.name}`}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, marginBottom: 5 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <div style={{ width: 20, height: 20, borderRadius: "50%", background: "#f2efe9", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, fontWeight: 700, color: "#8b7355", fontFamily: "system-ui, sans-serif", flexShrink: 0 }}>
                {i + 1}
              </div>
              <div>
                <div style={{ fontSize: 13, color: "#1a1a1a", fontWeight: 500, fontFamily: "system-ui, sans-serif", lineHeight: 1.2 }}>
  {artist.name}
  {artist.count > 50 && (
    <span
      style={{
        marginLeft: 6,
        fontSize: 10,
        color: "#b91c1c",
        fontWeight: 600,
      }}
    >
      ⚠️ {dashboardT("complete.portfolio.volumeWarning")}
    </span>
  )}
</div>
                <div style={{ fontSize: 11, color: "#9ca3af", fontFamily: "system-ui, sans-serif" }}>
                  {topArtistsMode === "count"
                    ? `${dashboardT("complete.portfolio.artworkCount", { count: artist.count || 0 })} • ${compactEuro(artist.value)}`
                    : `${compactEuro(artist.value)} • ${dashboardT("complete.portfolio.artworkCount", { count: artist.count || 0 })}`}
                </div>
              </div>
            </div>
            <div style={{ fontSize: 13, color: "#8b7355", fontWeight: 600, fontFamily: "system-ui, sans-serif" }}>
              {artist.share}%
            </div>
          </div>

          <div style={{ height: 4, background: "#f2efe9", borderRadius: 999 }}>
            <div style={{ width: `${Math.max(artist.share, 3)}%`, height: "100%", background: "#c9a961", borderRadius: 999 }} />
          </div>
        </div>
      ))
  ) : (
    <div style={{ fontSize: 13, color: "#9ca3af", fontFamily: "system-ui, sans-serif", padding: "8px 0" }}>
      {dashboardT("complete.portfolio.artistsEmpty")}
    </div>
  )}

{(
  (topArtistsMode === "count"
    ? portfolioFocusResolved?.topArtistsByCount
    : portfolioFocusResolved?.topArtists) || []
).length > 5 && (
  <button
    type="button"
    onClick={() => setShowAllTopArtists((v) => !v)}
    style={{
      border: "none",
      background: "transparent",
      padding: 0,
      fontSize: 12,
      color: "#8b7355",
      fontFamily: "system-ui, sans-serif",
      cursor: "pointer",
      textAlign: "left",
    }}
  >
    {showAllTopArtists
      ? dashboardT("portfolio.reduce")
      : dashboardT("portfolio.showTopArtists", {
          count: Math.min(
            10,
            (
              topArtistsMode === "count"
                ? portfolioFocusResolved?.topArtistsByCount
                : portfolioFocusResolved?.topArtists
            )?.length || 0
          ),
        })}
  </button>
)}

</div>
            </div>

            <div style={{ background: "#fff", border: "1px solid #ece9e4", borderRadius: 6, padding: 18 }}>
              <div style={{ fontSize: 12, textTransform: "uppercase", letterSpacing: "0.06em", color: "#9ca3af", fontFamily: "system-ui, sans-serif", marginBottom: 12 }}>
                {dashboardT("portfolio.categoryBreakdown")}
              </div>
              {(portfolioFocusResolved?.categoryDistribution || []).length > 0 ? (() => {
                const colors = ["#c9a961", "#8b7355", "#d9c48f", "#a68a64", "#e7ddc5", "#6f604c", "#b7a17a", "#efe8d8"];
                let cursor = 0;
                const segments = (portfolioFocusResolved.categoryDistribution || []).map((category, index) => {
                  const from = cursor;
                  const to = cursor + (category.share || 0);
                  cursor = to;
                  return `${colors[index % colors.length]} ${from}% ${to}%`;
                }).join(", ");
                return (
                  <>
                    <div style={{ display: "flex", alignItems: "center", gap: 18, marginBottom: 16 }}>
                      <div aria-label={dashboardT("complete.portfolio.categoryChartAria")} style={{ width: 118, height: 118, borderRadius: "50%", background: `conic-gradient(${segments})`, boxShadow: "inset 0 0 0 18px #fff, 0 0 0 1px #ece9e4", flexShrink: 0 }} />
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 26, fontWeight: 300, color: "#1a1a1a", fontFamily: "Georgia, serif", lineHeight: 1.1 }}>{portfolioFocusResolved?.dominantCategory?.name || "—"}</div>
                        <div style={{ fontSize: 12, color: "#6b6b6b", lineHeight: 1.6, fontFamily: "system-ui, sans-serif", marginTop: 8 }}>{dashboardT("complete.portfolio.dominantCategoryNote", { count: portfolioFocusResolved?.distinctCategories || 0 })}</div>
                      </div>
                    </div>
                    <div style={{ display: "flex", flexDirection: "column", gap: 9 }}>
                      {portfolioFocusResolved.categoryDistribution.slice(0, 5).map((category, index) => (
                        <div key={category.name} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, fontFamily: "system-ui, sans-serif", fontSize: 13 }}>
                          <span style={{ display: "flex", alignItems: "center", gap: 8, color: "#1a1a1a" }}><span style={{ width: 9, height: 9, borderRadius: "50%", background: colors[index % colors.length], display: "inline-block" }} />{category.name}</span>
                          <span style={{ color: "#6b6b6b" }}>{category.share}%</span>
                        </div>
                      ))}
                    </div>
                  </>
                );
              })() : <div style={{ fontSize: 13, color: "#9ca3af", fontFamily: "system-ui, sans-serif" }}>{dashboardT("complete.portfolio.categoryDistributionEmpty")}</div>}
            </div>

            <div style={{ background: "#fff", border: "1px solid #ece9e4", borderRadius: 6, padding: 18 }}>
              <div style={{ fontSize: 12, textTransform: "uppercase", letterSpacing: "0.06em", color: "#9ca3af", fontFamily: "system-ui, sans-serif", marginBottom: 12 }}>{dashboardT("portfolio.categoryValueAllocation")}</div>
              <div style={{ fontSize: 13, color: "#6b6b6b", lineHeight: 1.6, fontFamily: "system-ui, sans-serif", marginBottom: 14 }}>{portfolioFocusResolved?.categoryAllocationInsight || dashboardT("complete.portfolio.categoryAllocationEmpty")}</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                {(portfolioFocusResolved?.categoryValueDistribution || []).length > 0 ? portfolioFocusResolved.categoryValueDistribution.slice(0, 5).map((category) => (
                  <div key={category.name}>
                    <div style={{ display: "flex", justifyContent: "space-between", gap: 12, marginBottom: 6, fontFamily: "system-ui, sans-serif", fontSize: 13 }}>
                      <span style={{ color: "#1a1a1a" }}>{category.name}</span>
                      <span style={{ color: category.deltaShare > 10 ? "#b7791f" : "#6b6b6b" }}>{dashboardT("complete.portfolio.valueVsArtworks", { valueShare: category.valueShare, artworkShare: category.share })}</span>
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6 }}>
                      <div style={{ height: 6, background: "#f2efe9", borderRadius: 999, overflow: "hidden" }} title={dashboardT("complete.portfolio.artworksShareTitle")}><div style={{ width: `${Math.max(category.share, 3)}%`, height: "100%", background: "#d9c48f" }} /></div>
                      <div style={{ height: 6, background: "#f2efe9", borderRadius: 999, overflow: "hidden" }} title={dashboardT("complete.portfolio.valueShareTitle")}><div style={{ width: `${Math.max(category.valueShare, 3)}%`, height: "100%", background: "#c9a961" }} /></div>
                    </div>
                  </div>
                )) : <div style={{ fontSize: 13, color: "#9ca3af", fontFamily: "system-ui, sans-serif" }}>{dashboardT("complete.portfolio.valueVolumeEmpty")}</div>}
              </div>
            </div>

            <div style={{ background: "#fff", border: "1px solid #ece9e4", borderRadius: 6, padding: 18 }}>
              <div style={{ fontSize: 12, textTransform: "uppercase", letterSpacing: "0.06em", color: "#9ca3af", fontFamily: "system-ui, sans-serif", marginBottom: 12 }}>{dashboardT("portfolio.techniqueBreakdown")}</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                {(portfolioFocusResolved?.topTechniques || []).length > 0 ? portfolioFocusResolved.topTechniques.map((technique) => (
                  <div key={technique.name}>
                    <div style={{ display: "flex", justifyContent: "space-between", gap: 12, marginBottom: 6, fontFamily: "system-ui, sans-serif", fontSize: 13 }}><span style={{ color: "#1a1a1a" }}>{formatCanonicalTechniqueLabel(technique.name)}</span><span style={{ color: "#6b6b6b" }}>{technique.share}%</span></div>
                    <div style={{ height: 6, background: "#f2efe9", borderRadius: 999, overflow: "hidden" }}><div style={{ width: `${Math.max(technique.share, 4)}%`, height: "100%", background: "#c9a961" }} /></div>
                  </div>
                )) : <div style={{ fontSize: 13, color: "#9ca3af", fontFamily: "system-ui, sans-serif" }}>{dashboardT("complete.portfolio.techniquesEmpty")}</div>}
              </div>
            </div>
          </div>
        </>
      )}
      </>
    )}


</>
)}
          {tab === "missions" && (
            <Suspense fallback={<div style={{ padding: 48, textAlign: "center", color: "#6b6b6b", fontFamily: "system-ui, sans-serif" }}>{dashboardT("complete.common.loading")}</div>}>
              <AdvisorMissions />
            </Suspense>
          )}
          {tab === "collections" && (
            <div>
              <div style={{ background: "#f5f5f4", border: "1px solid #e5e3df", borderRadius: 6, padding: 24, marginBottom: 24 }}>
                <h3 style={{ fontWeight: 400, fontSize: 18, color: "#1a1a1a", marginBottom: 12, letterSpacing: "-0.01em" }}>
                  {dashboardT("complete.collections.title")}
                </h3>
                <p style={{ fontSize: 14, lineHeight: 1.7, color: "#4a4a4a", fontFamily: "system-ui, -apple-system, sans-serif", marginBottom: 0 }}>
                  {dashboardT("complete.collections.description")}
                </p>
              </div>

              <SharedErrorBoundary message={dashboardT("complete.errors.sharedDashboardLoad")}>
                <Suspense
                  fallback={
                    <div style={{ padding: 32, background: "#fff", border: "1px solid #e5e3df", borderRadius: 6, textAlign: "center", color: "#6b6b6b", fontFamily: "system-ui, -apple-system, sans-serif", fontSize: 14 }}>
                      {t("expert.Dashboard.shared.loadingCollections")}
                    </div>
                  }
                >
                  <SharedDashboard />
                </Suspense>
              </SharedErrorBoundary>

              <SharedErrorBoundary message={dashboardT("complete.errors.sharedDashboardLoad")}>
                <Suspense fallback={<div style={{ padding: 16 }}>{t("expert.Dashboard.news.loading")}</div>}>
                  <ExpertActualitesModule />
                </Suspense>
              </SharedErrorBoundary>
            </div>
          )}

          {tab === "services" && (
            <div>
              <div style={{ background: "#fffef7", border: "1px solid #f5e6d3", borderRadius: 6, padding: 24, marginBottom: 32 }}>
                <h3 style={{ fontWeight: 400, fontSize: 20, color: "#1a1a1a", marginBottom: 12, fontFamily: "Georgia, serif", letterSpacing: "-0.01em" }}>
                  {dashboardT("complete.services.pageTitle")}
                </h3>
                <p style={{ fontSize: 14, lineHeight: 1.7, color: "#4a4a4a", fontFamily: "system-ui, sans-serif", marginBottom: 0 }}>
                  {dashboardT("complete.services.pageSubtitle")}
                </p>
              </div>

              {errorMsg && (
                <div style={{ marginBottom: 20, color: "#b91c1c", padding: 16, background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 6, fontFamily: "system-ui, sans-serif", fontSize: 14 }}>
                  {errorMsg}
                </div>
              )}

              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(260px, 100%), 1fr))", gap: 20, width: "100%", alignItems: "start" }}>
                {/* Nouveau */}
                <div>
                  <div style={{ background: "#eff6ff", border: "1px solid #dbeafe", borderRadius: 8, padding: 16, marginBottom: 16, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <div style={{ fontFamily: "system-ui, sans-serif", fontSize: 14, fontWeight: 600, color: "#1e3a8a", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                      {dashboardT("complete.services.columns.new")}
                    </div>
                    <div style={{ background: "#1e3a8a", color: "#fff", width: 24, height: 24, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 600 }}>
                      {stats.pending}
                    </div>
                  </div>
                  <div>
                    {serviceRequests.filter((r) => (r.status ?? "pending") === "pending").map((r) => (
                      <ServiceKanbanCard key={r.id} request={r} />
                    ))}
                    {stats.pending === 0 && (
                      <div style={{ padding: 24, textAlign: "center", color: "#9ca3af", fontSize: 13, fontFamily: "system-ui, sans-serif", border: "1px dashed #e5e3df", borderRadius: 8, background: "#fafaf8" }}>
                        {dashboardT("complete.services.empty.new")}
                      </div>
                    )}
                  </div>
                </div>

                {/* En cours */}
                <div>
                  <div style={{ background: "#fef9e7", border: "1px solid #fde68a", borderRadius: 8, padding: 16, marginBottom: 16, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <div style={{ fontFamily: "system-ui, sans-serif", fontSize: 14, fontWeight: 600, color: "#92400e", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                      {dashboardT("complete.services.columns.inProgress")}
                    </div>
                    <div style={{ background: "#92400e", color: "#fff", width: 24, height: 24, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 600 }}>
                      {stats.in_progress}
                    </div>
                  </div>
                  <div>
                    {serviceRequests.filter((r) => r.status === "in_progress").map((r) => (
                      <ServiceKanbanCard key={r.id} request={r} />
                    ))}
                    {stats.in_progress === 0 && (
                      <div style={{ padding: 24, textAlign: "center", color: "#9ca3af", fontSize: 13, fontFamily: "system-ui, sans-serif", border: "1px dashed #e5e3df", borderRadius: 8, background: "#fafaf8" }}>
                        {dashboardT("complete.services.empty.inProgress")}
                      </div>
                    )}
                  </div>
                </div>

                {/* Attente retour */}
                <div>
                  <div style={{ background: "#fff7ed", border: "1px solid #fed7aa", borderRadius: 8, padding: 16, marginBottom: 16, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <div style={{ fontFamily: "system-ui, sans-serif", fontSize: 13, fontWeight: 600, color: "#c2410c", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                      {dashboardT("complete.services.columns.awaitingFeedback")}
                    </div>
                    <div style={{ background: "#c2410c", color: "#fff", width: 24, height: 24, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 600 }}>
                      {stats.awaiting_feedback || 0}
                    </div>
                  </div>
                  <div>
                    {serviceRequests.filter((r) => r.status === "awaiting_feedback").map((r) => (
                      <ServiceKanbanCard key={r.id} request={r} />
                    ))}
                    {(stats.awaiting_feedback || 0) === 0 && (
                      <div style={{ padding: 24, textAlign: "center", color: "#9ca3af", fontSize: 13, fontFamily: "system-ui, sans-serif", border: "1px dashed #e5e3df", borderRadius: 8, background: "#fafaf8" }}>
                        {dashboardT("complete.services.empty.awaitingFeedback")}
                      </div>
                    )}
                  </div>
                </div>

                {/* Terminé */}
                <div>
                  <div style={{ background: "#ecfdf5", border: "1px solid #d1fae5", borderRadius: 8, padding: 16, marginBottom: 16, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <div style={{ fontFamily: "system-ui, sans-serif", fontSize: 14, fontWeight: 600, color: "#065f46", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                      {dashboardT("complete.services.columns.completed")}
                    </div>
                    <div style={{ background: "#065f46", color: "#fff", width: 24, height: 24, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 600 }}>
                      {stats.completed}
                    </div>
                  </div>
                  <div>
                    {serviceRequests.filter((r) => r.status === "completed").map((r) => (
                      <ServiceKanbanCard key={r.id} request={r} />
                    ))}
                    {stats.completed === 0 && (
                      <div style={{ padding: 24, textAlign: "center", color: "#9ca3af", fontSize: 13, fontFamily: "system-ui, sans-serif", border: "1px dashed #e5e3df", borderRadius: 8, background: "#fafaf8" }}>
                        {dashboardT("complete.services.empty.completed")}
                      </div>
                    )}
                  </div>
                </div>

                {/* Annulé */}
                <div>
                  <div style={{ background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 2, padding: 16, marginBottom: 16, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <div style={{ fontFamily: "system-ui, sans-serif", fontSize: 14, fontWeight: 600, color: "#991b1b", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                      {dashboardT("complete.services.columns.cancelled")}
                    </div>
                    <div style={{ background: "#991b1b", color: "#fff", width: 24, height: 24, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 600 }}>
                      {stats.cancelled}
                    </div>
                  </div>
                  <div>
                    {serviceRequests.filter((r) => r.status === "cancelled").map((r) => (
                      <ServiceKanbanCard key={r.id} request={r} />
                    ))}
                    {stats.cancelled === 0 && (
                      <div style={{ padding: 24, textAlign: "center", color: "#9ca3af", fontSize: 13, fontFamily: "system-ui, sans-serif", border: "1px dashed #e5e3df", borderRadius: 6, background: "#fafaf8" }}>
                        {dashboardT("complete.services.empty.cancelled")}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {tab === "clients" && (
            <div style={{ padding: "32px 0" }}>
              <AdvisorClientDossiers />
              <div style={{ background: "#fff", border: "1px solid #e5e3df", borderRadius: 2, padding: 24, marginBottom: 24, boxShadow: "0 1px 3px rgba(0,0,0,0.04)" }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16, marginBottom: 16 }}>
                  <div>
                    <h2 style={{ fontFamily: "Georgia, serif", fontSize: 20, fontWeight: 400, color: "#1a1a1a", margin: 0, letterSpacing: "-0.02em" }}>
                      {t("expert.Dashboard.clientInvites.title")}
                    </h2>
                    <div style={{ marginTop: 6, fontFamily: "system-ui, sans-serif", fontSize: 13, color: "#6b6b6b" }}>
                      {t("expert.Dashboard.clientInvites.subtitle")}
                    </div>
                  </div>

                  <div style={{ fontFamily: "system-ui, sans-serif", fontSize: 12, color: "#6b6b6b", display: "flex", alignItems: "center", gap: 8 }}>
                    <span style={{ padding: "6px 10px", borderRadius: 999, border: "1px solid #e5e3df", background: "#fafaf8", fontWeight: 600, color: "#1a1a1a" }}>
                      {clientInvitesCount || 0}
                    </span>
                    <button
                      type="button"
                      onClick={() => loadClientInvites()}
                      style={{ padding: "8px 10px", borderRadius: 2, border: "1px solid #e5e3df", background: "#fff", cursor: "pointer", fontFamily: "system-ui, sans-serif", fontSize: 12, fontWeight: 500 }}
                      title={t("common.refresh")}
                    >
                      {t("common.refresh")}
                    </button>
                  </div>
                </div>

                {clientInvitesCount === 0 ? (
                  <div style={{ padding: 16, border: "1px dashed #e5e3df", borderRadius: 2, background: "#fafaf8", fontFamily: "system-ui, sans-serif", fontSize: 13, color: "#6b6b6b" }}>
                    {t("expert.Dashboard.clientInvites.empty")}
                  </div>
                ) : (
                  <div style={{ display: "grid", gap: 12 }}>
                    {clientInvites.map((inv) => {
                      const fn = (inv?.client_profile?.first_name || "").trim();
                      const ln = (inv?.client_profile?.last_name || "").trim();
                      const clientName =
                        `${fn} ${ln}`.trim() ||
                        (inv?.client_id ? dashboardT("complete.services.collectorWithId", { id: String(inv.client_id).slice(0, 8) }) : t("expert.Dashboard.clientInvites.unknownClient"));

                      return (
                        <div
                          key={inv.id}
                          style={{
                            padding: 16,
                            border: "3px solid #dc2626",
                            borderRadius: 2,
                            background: "#fef2f2",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            gap: 16,
                            boxShadow: "0 4px 6px rgba(220, 38, 38, 0.15)",
                          }}
                        >
                          <div style={{ flex: 1 }}>
                            <div style={{ fontFamily: "system-ui, sans-serif", fontSize: 14, fontWeight: 600, color: "#1a1a1a" }}>
                              {clientName}
                            </div>

                            {inv?.message && (
                              <div style={{ marginTop: 6, fontFamily: "system-ui, sans-serif", fontSize: 13, color: "#4a4a4a", lineHeight: 1.5 }}>
                                <span style={{ color: "#6b6b6b" }}>
                                  {t("expert.Dashboard.clientInvites.messageLabel")}:
                                </span>{" "}
                                {inv.message}
                              </div>
                            )}

                            {inv?.invited_at && (
                              <div style={{ marginTop: 6, fontFamily: "system-ui, sans-serif", fontSize: 12, color: "#6b6b6b" }}>
                                {t("expert.Dashboard.clientInvites.invitedAt")}{" "}
                                {new Date(inv.invited_at).toLocaleDateString(locale)}
                              </div>
                            )}

                            {inv?.origin === "platform_client" && (
                              <div
                                style={{
                                  marginTop: 8,
                                  display: "inline-block",
                                  padding: "4px 10px",
                                  borderRadius: 999,
                                  fontFamily: "system-ui, sans-serif",
                                  fontSize: 12,
                                  fontWeight: 600,
                                  background: introIncluded ? "#ecfdf5" : "#fff7ed",
                                  border: introIncluded ? "1px solid #10b981" : "1px solid #fed7aa",
                                  color: introIncluded ? "#065f46" : "#c2410c",
                                }}
                              >
                                {introIncluded
                                  ? t("expert.Dashboard.clientInvites.introFee.includedBadge")
                                  : t("expert.Dashboard.clientInvites.introFee.feeBadge")}
                              </div>
                            )}
                          </div>

                          <div style={{ display: "flex", gap: 8 }}>
                            <button
                              type="button"
                              onClick={() => rejectClientInvite(inv.id)}
                              style={{ padding: "10px 12px", borderRadius: 2, border: "1px solid #e5e3df", background: "#fff", cursor: "pointer", fontFamily: "system-ui, sans-serif", fontSize: 12, fontWeight: 600 }}
                            >
                              {t("common.reject")}
                            </button>
                            <button
                              type="button"
                              onClick={() => acceptClientInvite(inv.id)}
                              style={{ padding: "10px 12px", borderRadius: 2, border: "1px solid #111", background: "#111", color: "#fff", cursor: "pointer", fontFamily: "system-ui, sans-serif", fontSize: 12, fontWeight: 600 }}
                            >
                              {t("common.accept")}
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              <Suspense fallback={<div style={{ textAlign: "center", padding: 48, color: "#6b6b6b", fontFamily: "system-ui, sans-serif" }}>{dashboardT("complete.common.loading")}</div>}>
                <ClientManagement />
              </Suspense>
            </div>
          )}

          {tab === "outils" && (
            <div>
              {/* ── En-tête ── */}
              <div style={{ background: "#f5f5f4", border: "1px solid #e5e3df", borderRadius: 6, padding: 24, marginBottom: 32 }}>
                <h3 style={{ fontFamily: "Georgia, serif", fontWeight: 400, fontSize: 20, color: "#1a1a1a", marginBottom: 8 }}>
                  {dashboardT("complete.tools.title")}
                </h3>
                <p style={{ fontSize: 14, color: "#4a4a4a", fontFamily: "system-ui, sans-serif", lineHeight: 1.6, margin: 0 }}>
                  {dashboardT("complete.tools.organizedSubtitle")}
                </p>
              </div>

              {(() => {
                const toolSections = [
                  {
                    key: "prepare",
                    title: dashboardT("complete.tools.sections.prepare.title"),
                    description: dashboardT("complete.tools.sections.prepare.description"),
                    accent: "#34516a",
                    soft: "#eff5f8",
                    kicker: dashboardT("complete.tools.categories.prepare"),
                    tools: [
                      { key: "artprice", to: "/expert/import-artprice", icon: ExternalLink },
                      { key: "artistQuotes", to: "/expert/artist-quotes", icon: TrendingUp },
                      { key: "warehouse", to: "/visite", icon: Warehouse },
                    ],
                  },
                  {
                    key: "analyze",
                    title: dashboardT("complete.tools.sections.analyze.title"),
                    description: dashboardT("complete.tools.sections.analyze.description"),
                    accent: "#b78d3f",
                    soft: "#fbf6e8",
                    kicker: dashboardT("complete.tools.categories.analysis"),
                    tools: [
                      { key: "comparables", to: "/estimation-semblables", icon: TrendingUp },
                      {
                        key: "inflation",
                        to: "/inflation-report",
                        icon: TrendingUp,
                        cta: dashboardT("complete.tools.inflation.calculateCta"),
                      },
                      {
                        key: "curatorial",
                        href: VISION_URL,
                        icon: FileText,
                        badge: dashboardT("complete.tools.badges.vision"),
                        badgeColor: "#7c3aed",
                      },
                    ],
                  },
                  {
                    key: "deliver",
                    title: dashboardT("complete.tools.sections.deliver.title"),
                    description: dashboardT("complete.tools.sections.deliver.description"),
                    accent: "#2f6b4f",
                    soft: "#eef7f1",
                    kicker: dashboardT("complete.tools.categories.report"),
                    tools: [
                      { key: "inventory", to: "/collections-reports", icon: BarChart2 },
                      { key: "valuation", to: "/expert/rapport-valorisation", icon: BookOpen, badge: dashboardT("complete.tools.badges.advisorPremium") },
                      { key: "conservation", to: "/collections-reports", icon: AlertTriangle },
                    ],
                  },
                ];

                return toolSections.map((section, sectionIndex) => (
                  <section key={section.key} style={{ marginBottom: sectionIndex === toolSections.length - 1 ? 0 : 38 }}>
                    <div style={{ marginBottom: 16, paddingBottom: 12, borderBottom: `1px solid ${section.soft}` }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 5 }}>
                        <span style={{ width: 7, height: 7, borderRadius: "50%", background: section.accent, flexShrink: 0 }} />
                        <h4 style={{ margin: 0, fontFamily: "Georgia, serif", fontSize: 18, fontWeight: 400, color: "#1a1a1a" }}>
                          {section.title}
                        </h4>
                      </div>
                      <p style={{ margin: 0, paddingLeft: 17, fontFamily: "system-ui, sans-serif", fontSize: 13, lineHeight: 1.55, color: "#6b6b6b" }}>
                        {section.description}
                      </p>
                    </div>

                    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(260px, 100%), 1fr))", gap: 16 }}>
                      {section.tools.map((tool) => {
                        const Icon = tool.icon;
                        const title = dashboardT(`complete.tools.${tool.key}.title`);
                        const description = dashboardT(`complete.tools.${tool.key}.description`);
                        const cta = tool.cta || dashboardT(`complete.tools.${tool.key}.cta`);
                        const cardContent = (
                          <>
                            <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 12 }}>
                              <div style={{ width: 40, height: 40, borderRadius: 6, background: section.soft, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                                <Icon size={20} color={section.accent} />
                              </div>
                              <div style={{ minWidth: 0 }}>
                                <div style={{ fontFamily: "Georgia, serif", fontSize: 16, fontWeight: 400, color: "#1a1a1a", lineHeight: 1.35 }}>
                                  {title}
                                </div>
                                <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 7, marginTop: 3 }}>
                                  <span style={{ fontSize: 10, color: section.accent, fontFamily: "system-ui, sans-serif", textTransform: "uppercase", letterSpacing: "0.08em" }}>
                                    {section.kicker}
                                  </span>
                                  {tool.badge && (
                                    <span style={{ fontSize: 9, color: tool.badgeColor || section.accent, border: `1px solid ${tool.badgeColor || section.accent}33`, borderRadius: 999, padding: "2px 6px", fontFamily: "system-ui, sans-serif", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                                      {tool.badge}
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                            <p style={{ fontSize: 13, color: "#6b6b6b", fontFamily: "system-ui, sans-serif", lineHeight: 1.6, margin: "0 0 18px", display: "-webkit-box", WebkitLineClamp: 4, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
                              {description}
                            </p>
                            <span style={{ display: "inline-flex", alignItems: "center", alignSelf: "flex-start", gap: 7, marginTop: "auto", padding: "9px 15px", background: section.accent, color: "#fff", borderRadius: 4, fontSize: 12, fontFamily: "system-ui, sans-serif", fontWeight: 600 }}>
                              <Icon size={14} /> {cta}{tool.href ? " ↗" : ""}
                            </span>
                          </>
                        );
                        const cardStyle = {
                          textDecoration: "none",
                          color: "inherit",
                          display: "flex",
                          flexDirection: "column",
                          minHeight: 248,
                          background: "#fff",
                          border: "1px solid #e5e3df",
                          borderLeft: `3px solid ${section.accent}`,
                          borderRadius: 6,
                          padding: 24,
                        };

                        return tool.href ? (
                          <a key={tool.key} href={tool.href} target="_blank" rel="noopener noreferrer" style={cardStyle}>
                            {cardContent}
                          </a>
                        ) : (
                          <Link key={tool.key} to={tool.to} style={cardStyle}>
                            {cardContent}
                          </Link>
                        );
                      })}
                    </div>
                  </section>
                ));
              })()}
            </div>
          )}

          {tab === "collaborateurs" && (
            <div style={{ padding: "32px 40px" }}>
              {/* ── En-tête ── */}
              <div style={{ marginBottom: 28 }}>
                <p style={{ fontFamily: "Georgia, serif", fontSize: 13, color: "#9a8c7e", letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 6 }}>
                  {dashboardT("actions.collaborateurs")}
                </p>
                <h2 style={{ fontFamily: "Georgia, serif", fontWeight: 400, fontSize: 26, color: "#1a1a1a", margin: "0 0 8px" }}>
                  {dashboardT("collaborateurs.title")}
                </h2>
                <p style={{ fontFamily: "system-ui, sans-serif", fontSize: 14, color: "#6b6b6b", lineHeight: 1.6, maxWidth: 580, margin: 0 }}>
                  {dashboardT("collaborateurs.subtitle")}
                </p>
              </div>

              {/* ── Quota plan ── */}
              {(() => {
                const planLimits = { standard: 1, premium: 5, art_advisor: 0, art_advisor_premium: 3, family_office: 999 };
                const max = planLimits[profile?.plan ?? "premium"] ?? 1;
                return (
                  <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 28, padding: "12px 16px", background: "#f9f7f4", border: "1px solid #e5e3df", borderRadius: 2 }}>
                    <UserPlus size={16} color="#c9a961" />
                    <span style={{ fontFamily: "system-ui, sans-serif", fontSize: 13, color: "#4a4a4a" }}>
                      {dashboardT("collaborateurs.quotaLabel")} <strong>
  { { standard: "Standard", premium: "Premium", art_advisor: "Art Advisor", art_advisor_premium: "Art Advisor Premium", family_office: "Family Office" }[profile?.plan] || "Premium" }
</strong> —{" "}
                      {max === 999
                        ? dashboardT("collaborateurs.quotaUnlimited")
                        : dashboardT("collaborateurs.quotaMax").replace("{{count}}", max)}
                    </span>
                  </div>
                );
              })()}

              {/* ── CollaboratorsManager ── */}
              <CollaboratorsManager
                userId={user.id}
                userPlan={profile?.plan || "premium"}
                collectionId={null}
              />
            </div>
          )}

          {tab === "billing" && (
            <div style={{ padding: 0 }}>
              <Suspense fallback={<div style={{ textAlign: "center", padding: 48, fontFamily: "Georgia, serif", color: "#6b6b6b" }}>{dashboardT("complete.common.loading")}</div>}>
                <ExpertBillingManager />
              </Suspense>
            </div>
          )}

          {tab === "profile" && (
            <div>
              <ExpertProfileTab
                expertRow={expertRow}
                profileForm={profileForm}
                setProfileForm={setProfileForm}
                savingExpert={savingExpert}
                uploadingPhoto={uploadingPhoto}
                uploadingCv={uploadingCv}
                photoInputRef={photoInputRef}
                cvInputRef={cvInputRef}
                uploadExpertPhoto={uploadExpertPhoto}
                uploadCvAndScan={uploadCvAndScan}
                upsertExpertProfile={upsertExpertProfile}
                loadExpertProfile={loadExpertProfile}
                togglePublicPresence={togglePublicPresence}
                DEFAULT_EXPERT_IMAGE={DEFAULT_EXPERT_IMAGE}
              />
              <div className="mt-6">
                <ExpertIdentitySection
                  userId={user.id}
                  profile={profileBasics}
                  onSaved={(updated) => setProfileBasics((prev) => ({ ...prev, ...updated }))}
                />
              </div>
              <div className="mt-6"><AdvisorBillingIdentity /></div>
            </div>
          )}
        </div>
      </div>

      {showAcceptModal && requestToAccept && (
        <div
          style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, background: "rgba(0, 0, 0, 0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 9999, padding: 24 }}
          onClick={() => {
            setShowAcceptModal(false);
            setRequestToAccept(null);
          }}
        >
          <div
            style={{ background: "#fff", borderRadius: 2, padding: 32, maxWidth: 500, width: "100%", boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)" }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: "flex", alignItems: "flex-start", gap: 16, marginBottom: 24 }}>
              <div style={{ width: 48, height: 48, borderRadius: "50%", background: "#fef2f2", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                <AlertTriangle size={24} color="#dc2626" />
              </div>
              <div style={{ flex: 1 }}>
                <h3 style={{ fontFamily: "Georgia, serif", fontSize: 20, fontWeight: 500, color: "#1a1a1a", marginBottom: 8 }}>
                  {dashboardT("complete.acceptContact.title")}
                </h3>
                <p style={{ fontFamily: "system-ui, sans-serif", fontSize: 14, color: "#6b6b6b", lineHeight: 1.6, marginBottom: 16 }}>
                  <Trans
                    i18nKey="expert.Dashboard.complete.acceptContact.body"
                    components={{
                      free: <strong style={{ color: "#065f46" }} />,
                    }}
                  />
                </p>
              </div>
            </div>

            <div style={{ display: "flex", gap: 12, justifyContent: "flex-end" }}>
              <button
                type="button"
                onClick={() => {
                  setShowAcceptModal(false);
                  setRequestToAccept(null);
                }}
                style={{ padding: "10px 20px", borderRadius: 2, border: "1px solid #e5e3df", background: "#fff", color: "#6b6b6b", fontFamily: "system-ui, sans-serif", fontSize: 14, fontWeight: 500, cursor: "pointer" }}
              >
                {dashboardT("complete.common.cancel")}
              </button>
              <button
                type="button"
                onClick={() => acceptClient(requestToAccept)}
                style={{ padding: "10px 20px", borderRadius: 2, border: "none", background: "#065f46", color: "#fff", fontFamily: "system-ui, sans-serif", fontSize: 14, fontWeight: 600, cursor: "pointer" }}
              >
                {dashboardT("complete.acceptContact.confirm")}
              </button>
            </div>
          </div>
        </div>
      )}

      {introModal && (
        <div
          style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 9999, padding: 24 }}
          onClick={() => { if (!introBusy) setIntroModal(null); }}
        >
          <div
            style={{ background: "#fff", borderRadius: 2, padding: 32, maxWidth: 500, width: "100%", boxShadow: "0 20px 25px -5px rgba(0,0,0,0.1)" }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ fontFamily: "Georgia, serif", fontSize: 20, fontWeight: 500, color: "#1a1a1a", margin: "0 0 12px" }}>
              {t("expert.Dashboard.clientInvites.introFee.modalTitle")}
            </h3>
            <p style={{ fontFamily: "system-ui, sans-serif", fontSize: 14, color: "#4a4a4a", lineHeight: 1.6, margin: "0 0 14px" }}>
              <Trans
                i18nKey="expert.Dashboard.clientInvites.introFee.modalBody"
                components={{ price: <strong style={{ color: "#1a1a1a" }} /> }}
              />
            </p>
            <div style={{ padding: 12, background: "#ecfdf5", border: "1px solid #10b981", borderRadius: 2, fontSize: 13, fontFamily: "system-ui, sans-serif", color: "#065f46", marginBottom: 20 }}>
              {t("expert.Dashboard.clientInvites.introFee.modalSubscribe")}{" "}
              <Link to="/pricing" style={{ color: "#065f46", fontWeight: 600 }}>
                {t("expert.Dashboard.clientInvites.introFee.seePlans")}
              </Link>
            </div>
            <div style={{ display: "flex", gap: 12, justifyContent: "flex-end" }}>
              <button
                type="button"
                disabled={introBusy}
                onClick={() => setIntroModal(null)}
                style={{ padding: "10px 20px", borderRadius: 2, border: "1px solid #e5e3df", background: "#fff", color: "#6b6b6b", fontFamily: "system-ui, sans-serif", fontSize: 14, fontWeight: 500, cursor: "pointer" }}
              >
                {dashboardT("complete.common.cancel")}
              </button>
              <button
                type="button"
                disabled={introBusy}
                onClick={() => startIntroPayment(introModal.inviteId)}
                style={{ padding: "10px 20px", borderRadius: 2, border: "none", background: "#111", color: "#fff", fontFamily: "system-ui, sans-serif", fontSize: 14, fontWeight: 600, cursor: introBusy ? "wait" : "pointer", opacity: introBusy ? 0.7 : 1 }}
              >
                {introBusy
                  ? t("expert.Dashboard.clientInvites.introFee.paying")
                  : t("expert.Dashboard.clientInvites.introFee.pay")}
              </button>
            </div>
          </div>
        </div>
      )}

      {showInvite && (
        <div
          style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 9999, padding: 24 }}
          onClick={() => setShowInvite(false)}
        >
          <div style={{ background: "#fff", borderRadius: 2, padding: 24, width: "100%", maxWidth: 520 }} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ margin: 0, marginBottom: 12, fontFamily: "Georgia, serif", fontSize: 18 }}>
              {t("expert.Dashboard.invites.title")}
            </h3>

            <div style={{ display: "grid", gap: 10 }}>
              <input
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                placeholder={dashboardT("complete.invites.emailPlaceholder")}
                style={{ padding: 12, borderRadius: 2, border: "1px solid #e5e3df", fontFamily: "system-ui, sans-serif" }}
              />
              <textarea
                value={inviteNote}
                onChange={(e) => setInviteNote(e.target.value)}
                placeholder={dashboardT("complete.invites.notePlaceholder")}
                rows={4}
                style={{ padding: 12, borderRadius: 2, border: "1px solid #e5e3df", fontFamily: "system-ui, sans-serif", resize: "vertical" }}
              />

              <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
                <button
                  type="button"
                  onClick={() => setShowInvite(false)}
                  style={{ padding: "10px 14px", borderRadius: 2, border: "1px solid #e5e3df", background: "#fff", fontFamily: "system-ui, sans-serif", cursor: "pointer" }}
                >
                  {dashboardT("complete.common.cancel")}
                </button>
                <button
                  type="button"
                  onClick={createInvite}
                  disabled={sendingInvite}
                  style={{ padding: "10px 14px", borderRadius: 2, border: "none", background: "#111", color: "#fff", fontFamily: "system-ui, sans-serif", cursor: "pointer", opacity: sendingInvite ? 0.7 : 1 }}
                >
                  {sendingInvite ? dashboardT("complete.common.sending") : dashboardT("complete.common.send")}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );

}
